import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Mic, 
  MicOff, 
  Image as ImageIcon, 
  ArrowLeft,
  Save,
  Eye,
  Undo,
  Redo,
  X,
  Loader,
  FileText,
  Video,
  Code,
  HelpCircle,
  Share2,
  Sparkles,
  AlertCircle,
  Clock,
  BookOpen,
  CheckCircle2,
  Sparkles as SparklesIcon,
  ChevronDown,
  ChevronRight,
  Trash2,
  Wand2,
  Paperclip,
  Edit,
  Bold,
  Italic,
  List,
  Link
} from 'lucide-react';
import { apiService, TutorialStep, API_BASE_URL, getValidToken } from '../services/api';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';
import MediaUploader, { MediaFile } from '../components/MediaUploader';
import MediaLibrary from '../components/MediaLibrary';
import { debounce } from '../utils/debounce';
import { useAuth } from '../contexts/AuthContext';
import { marked } from 'marked';
import FrameSelector from '../components/FrameSelector';
import CategoryManager from '../components/CategoryManager';
import GlossaryExtractionModal from '../components/GlossaryExtractionModal';
import { Settings } from 'lucide-react';

// Configure marked for better rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Helper to render markdown to HTML with stable string typing
const renderMarkdown = (markdown: string): string => {
  if (!markdown) return '';
  try {
    const anyMarked: any = marked as any;
    let html: string;
    if (typeof anyMarked.parse === 'function') {
      html = anyMarked.parse(markdown) as string;
    } else {
      html = (anyMarked(markdown) as unknown as string) || '';
    }
    return html || markdown;
  } catch (error) {
    console.error('Error rendering markdown:', error);
    return markdown;
  }
};

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  mediaUrls?: string[];
  createdSteps?: string[]; // IDs of steps created in this message
  updatedSteps?: string[]; // IDs of steps updated in this message
}

interface TutorialState {
  title: string;
  description: string;
  type: 'guide' | 'course' | 'presentation';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  category: string;
  product: string;
  language: 'en' | 'es';
  layout?: 'steps' | 'continuous'; // Layout mode: step-by-step navigation or continuous scroll
  steps: TutorialStep[];
}

interface HistoryState {
  past: TutorialState[];
  present: TutorialState;
  future: TutorialState[];
}

interface Template {
  id: string;
  name: string;
  description: string;
  structure: any;
}

const AIAssistedBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const isEditing = !!id;
  
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: "Hello! I'm here to help you create an amazing tutorial. Tell me what you'd like to teach, and I'll help you build it step by step. You can describe your tutorial idea, and I'll generate a draft for you.",
      timestamp: new Date()
    }
  ]);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [pendingMediaFiles, setPendingMediaFiles] = useState<MediaFile[]>([]);
  const [pendingMediaUrls, setPendingMediaUrls] = useState<string[]>([]);
  const [showMediaPlacementModal, setShowMediaPlacementModal] = useState(false);
  const [mediaPlacement, setMediaPlacement] = useState<{
    stepId: string | null;
    position: 'before' | 'after' | 'within' | 'new-step';
  } | null>(null);
  const [showSimilarContent, setShowSimilarContent] = useState(false);
  const [showDraftRecoveryModal, setShowDraftRecoveryModal] = useState(false);
  const [draftToRecover, setDraftToRecover] = useState<{ tutorialState: TutorialState; messages: Message[] } | null>(null);
  const [similarTutorials, setSimilarTutorials] = useState<any[]>([]);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [newlyAddedSteps, setNewlyAddedSteps] = useState<Set<string>>(new Set());
  const [updatedSteps, setUpdatedSteps] = useState<Set<string>>(new Set());
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [editingStepField, setEditingStepField] = useState<'title' | 'content' | null>(null);
  const [editingStepModal, setEditingStepModal] = useState<string | null>(null); // Step ID being edited in modal
  const [editingStepContent, setEditingStepContent] = useState<string>(''); // Temporary content for modal editor
  const [showGlossaryExtraction, setShowGlossaryExtraction] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [focusedStepId, setFocusedStepId] = useState<string | null>(null);
  const [templatePreview, setTemplatePreview] = useState<Template | null>(null);
  const [extractedFrames, setExtractedFrames] = useState<Array<{ url: string; timestamp: number; timeString: string }>>([]);
  const [frameSelectorOpen, setFrameSelectorOpen] = useState(false);
  const [frameSelectorStepId, setFrameSelectorStepId] = useState<string | null>(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set());
  const [workflowMode, setWorkflowMode] = useState<'overview' | 'focused'>('overview');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryObjects, setCategoryObjects] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [tutorialState, setTutorialState] = useState<TutorialState>({
    title: '',
    description: '',
    type: 'guide',
    difficulty: 'beginner', // Keep for backward compatibility but won't show in UI
    estimatedTime: '',
    category: '',
    product: '',
    language: 'en',
    layout: 'steps', // Default to step-by-step layout
    steps: []
  });

  const [history, setHistory] = useState<HistoryState>({
    past: [],
    present: {
      title: '',
      description: '',
      type: 'guide',
      difficulty: 'beginner',
      estimatedTime: '',
      category: 'Getting Started',
      product: '',
      language: 'en',
      layout: 'steps',
      steps: []
    },
    future: []
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Auto-save functionality
  const autoSaveDraft = useCallback(async (state: TutorialState, msgs: Message[]) => {
    try {
      const draftData = {
        tutorialState: state,
        messages: msgs,
        timestamp: Date.now(),
        tutorialId: id || null
      };
      localStorage.setItem('ai-builder-draft', JSON.stringify(draftData));
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  }, [id]);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (tutorialState.title || tutorialState.steps.length > 0) {
        autoSaveDraft(tutorialState, messages);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [tutorialState, messages, autoSaveDraft]);

  // Recover draft on mount (only once)
  useEffect(() => {
    if (!isEditing) {
      const draft = localStorage.getItem('ai-builder-draft');
      const draftRecoveryShown = sessionStorage.getItem('ai-builder-draft-recovery-shown');
      
      if (draft && !draftRecoveryShown) {
        try {
          const { tutorialState: draftState, messages: draftMessages, timestamp } = JSON.parse(draft);
          // Only recover if draft is less than 24 hours old
          if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
            setDraftToRecover({ tutorialState: draftState, messages: draftMessages });
            setShowDraftRecoveryModal(true);
            // Mark as shown to prevent it from appearing again in this session
            sessionStorage.setItem('ai-builder-draft-recovery-shown', 'true');
          } else {
            // Draft is too old, remove it
            localStorage.removeItem('ai-builder-draft');
          }
        } catch (error) {
          console.error('Failed to parse draft:', error);
          localStorage.removeItem('ai-builder-draft');
        }
      }
    }
  }, [isEditing]);

  const handleRecoverDraft = () => {
    if (draftToRecover) {
      setTutorialState(draftToRecover.tutorialState);
      setMessages(draftToRecover.messages);
      setHistory({
        past: [],
        present: draftToRecover.tutorialState,
        future: []
      });
      toast.success('Draft recovered');
      setShowDraftRecoveryModal(false);
      setDraftToRecover(null);
    }
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem('ai-builder-draft');
    setShowDraftRecoveryModal(false);
    setDraftToRecover(null);
    toast('Draft discarded', { icon: '🗑️' });
  };

  // Load templates
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadTemplates();
    }
  }, [authLoading, isAuthenticated]);

  // Load categories
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await apiService.getCategories();
      
      if (response.success && response.data) {
        const catData = response.data as any[];
        setCategoryObjects(catData);
        const categoryNames = catData.map((cat: any) => cat.name || cat);
        setCategories(categoryNames);
        // Set default category if none selected and categories exist
        if (!tutorialState.category && categoryNames.length > 0) {
          setTutorialState(prev => ({ ...prev, category: categoryNames[0] }));
        }
      } else {
        // Fallback to hardcoded categories if API fails
        setCategories(['Getting Started', 'Product Guides', 'API Docs', 'Workflows', 'Manufacturing']);
        if (!tutorialState.category) {
          setTutorialState(prev => ({ ...prev, category: 'Getting Started' }));
        }
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback to hardcoded categories
      setCategories(['Getting Started', 'Product Guides', 'API Docs', 'Workflows', 'Manufacturing']);
      if (!tutorialState.category) {
        setTutorialState(prev => ({ ...prev, category: 'Getting Started' }));
      }
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadTemplates = async () => {
    try {
      if (!isAuthenticated) {
        console.warn('Not authenticated, skipping template load');
        return;
      }

      const token = getValidToken(); // Get valid token (checks expiration automatically)
      if (!token) {
        console.warn('No auth token, skipping template load');
        return;
      }

      console.log('Loading templates in AI Builder...');
      const response = await fetch(`${API_BASE_URL}/templates`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Templates loaded in AI Builder:', data.templates?.length || 0);
        setTemplates(data.templates || []);
      } else {
        console.error('Failed to load templates:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error details:', errorData);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  // Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Voice Recognition Setup
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognition() as any;
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: any) => {
        // Accumulate all results, including interim ones
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join('');
        setInput(transcript);
        // Don't auto-send - wait for user to click send button
      };

      recognition.onerror = (event: any) => {
        // 'no-speech' is a benign error that occurs when user doesn't speak
        // It's expected behavior and shouldn't be logged as an error
        if (event.error === 'no-speech') {
          return; // Silently ignore - this is expected
        }
        console.error('Speech recognition error:', event.error);
        toast.error('Speech recognition error. Please try again.');
        setIsRecording(false);
      };

      recognition.onend = () => {
        // Auto-restart if still recording (continuous mode)
        // But only stop if user explicitly stopped
        // The stopRecording function will set isRecording to false
        setIsRecording((prev) => {
          // If we're still supposed to be recording, restart
          if (prev) {
            setTimeout(() => {
              if (recognitionRef.current) {
                try {
                  recognitionRef.current.start();
                } catch (e) {
                  // Ignore errors if already started
                }
              }
            }, 100);
          }
          return prev;
        });
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load existing tutorial if editing
  useEffect(() => {
    if (isEditing && id) {
      loadTutorial(id);
    }
  }, [isEditing, id]);

  const loadTutorial = async (tutorialId: string) => {
    try {
      setIsLoading(true);
      const tutorial = await apiService.getTutorialById(tutorialId);
      const state: TutorialState = {
        title: tutorial.title,
        description: tutorial.description || '',
        type: tutorial.type || 'guide',
        difficulty: tutorial.difficulty,
        estimatedTime: tutorial.estimatedTime,
        category: tutorial.category,
        product: tutorial.product || '',
        language: tutorial.language || 'en',
        layout: tutorial.layout || 'steps',
        steps: tutorial.steps || []
      };
      setTutorialState(state);
      setHistory({
        past: [],
        present: state,
        future: []
      });
      
      // Add a welcome message when editing
      setMessages([
        {
          id: '1',
          role: 'assistant',
          content: `I've loaded your tutorial "${tutorial.title}". You can now edit it using the AI assistant. Tell me what you'd like to change or improve!`,
          timestamp: new Date()
        }
      ]);
      
      toast.success('Tutorial loaded successfully');
    } catch (error) {
      console.error('Error loading tutorial:', error);
      toast.error('Failed to load tutorial');
    } finally {
      setIsLoading(false);
    }
  };

  const startRecording = () => {
    if (recognitionRef.current && !isRecording) {
      setIsRecording(true);
      recognitionRef.current.start();
    }
  };

  const stopRecording = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
  };

  const addToHistory = useCallback((newState: TutorialState) => {
    setHistory(prev => ({
      past: [...prev.past, prev.present],
      present: newState,
      future: []
    }));
  }, []);

  const undo = useCallback(() => {
    setHistory(prev => {
      if (prev.past.length === 0) return prev;
      const previous = prev.past[prev.past.length - 1];
      return {
        past: prev.past.slice(0, -1),
        present: previous,
        future: [prev.present, ...prev.future]
      };
    });
  }, []);

  const redo = useCallback(() => {
    setHistory(prev => {
      if (prev.future.length === 0) return prev;
      const next = prev.future[0];
      return {
        past: [...prev.past, prev.present],
        present: next,
        future: prev.future.slice(1)
      };
    });
  }, []);

  // Sync tutorialState with history.present
  useEffect(() => {
    setTutorialState(history.present);
  }, [history.present]);

  // Enhanced conversation management - keep more context
  const getConversationContext = useCallback((msgs: Message[], limit: number = 20) => {
    // Take last N messages but ensure we have pairs
    const recent = msgs.slice(-limit);
    // Summarize older messages if too many
    if (msgs.length > limit) {
      // Could add summarization here
    }
    return recent;
  }, []);

  // Streaming message handler
  const sendMessage = async (content?: string, mediaUrls?: string[], placement?: { stepId: string | null; position: 'before' | 'after' | 'within' | 'new-step' }) => {
    // Use pending media if available, or provided mediaUrls
    const mediaToSend = mediaUrls || (pendingMediaUrls.length > 0 ? pendingMediaUrls : undefined);
    const messageContent = content || input.trim();
    const placementInfo = placement || mediaPlacement;
    
    // Allow sending with just media if user has uploaded files
    if (!messageContent && !mediaToSend?.length) return;

    // Check if user is asking to analyze video and we have pending video files
    const videoFiles = pendingMediaFiles.filter(f => f.type.startsWith('video/'));
    const lowerMessage = messageContent.toLowerCase();
    const isVideoAnalysisRequest = videoFiles.length > 0 && messageContent && (
      (lowerMessage.includes('analyze') && lowerMessage.includes('video')) ||
      (lowerMessage.includes('create') && (lowerMessage.includes('tutorial') || lowerMessage.includes('guide')) && (lowerMessage.includes('video') || lowerMessage.includes('this'))) ||
      (lowerMessage.includes('guide') && (lowerMessage.includes('video') || lowerMessage.includes('this'))) ||
      (lowerMessage.includes('screenshot') && videoFiles.length > 0) ||
      (lowerMessage.includes('frame') && videoFiles.length > 0) ||
      (lowerMessage.includes('based on') && lowerMessage.includes('video')) ||
      (lowerMessage.includes('from this video')) ||
      (lowerMessage.includes('from the video')) ||
      (lowerMessage.includes('step by step') && (lowerMessage.includes('video') || videoFiles.length > 0))
    );

    // If user wants video analysis, trigger it first
    if (isVideoAnalysisRequest && videoFiles.length > 0) {
      console.log('🎬 Video analysis request detected!', {
        videoFiles: videoFiles.length,
        videoUrls: pendingMediaUrls.filter(url => /\.(mp4|mov|avi|webm|mkv)$/i.test(url)).length,
        message: messageContent.substring(0, 50) + '...'
      });
      
      // Add user message first
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: messageContent,
        timestamp: new Date(),
        mediaUrls: mediaToSend
      };
      setMessages(prev => [...prev, userMessage]);
      setInput('');
      
      // Get video URLs that match the video files
      const videoUrls = pendingMediaUrls.filter(url => /\.(mp4|mov|avi|webm|mkv)$/i.test(url));
      
      // Analyze videos - use URLs if available, otherwise convert files
      for (let i = 0; i < videoFiles.length; i++) {
        const videoFile = videoFiles[i];
        const videoUrl = videoUrls[i];
        
        console.log('🎬 Starting video analysis for:', videoFile.name, 'URL:', videoUrl);
        
        if (videoUrl) {
          // Use the uploaded file URL - fetch it and send to analyze endpoint
          await handleVideoAnalysis(videoFile, videoUrl);
        } else {
          // Fallback: convert base64 to file
          await handleVideoAnalysis(videoFile);
        }
      }
      
      // Clear pending media after analysis
      setPendingMediaFiles([]);
      setPendingMediaUrls([]);
      
      // After analysis, the tutorial state will be updated with steps and frames
      // The user can then continue the conversation with the analyzed content
      return; // Don't send the chat message yet - video analysis handles the response
    }

    // Additional check: If user has videos and is asking about them, don't send chat message
    // The video analysis should handle this instead
    const hasVideoInMessage = videoFiles.length > 0 || (mediaToSend && mediaToSend.some(url => /\.(mp4|mov|avi|webm|mkv)$/i.test(url)));
    const messageMentionsVideo = lowerMessage && (
      lowerMessage.includes('video') || 
      lowerMessage.includes('this') || 
      lowerMessage.includes('guide') ||
      lowerMessage.includes('tutorial')
    );
    
    if (hasVideoInMessage && messageMentionsVideo && !isVideoAnalysisRequest) {
      // User has video and is talking about it, but detection didn't trigger
      // This might be a video analysis request that didn't match the pattern
      // Add a more lenient check
      const mightBeVideoRequest = (
        lowerMessage.includes('create') || 
        lowerMessage.includes('analyze') || 
        lowerMessage.includes('guide') ||
        lowerMessage.includes('tutorial') ||
        lowerMessage.includes('step by step')
      );
      
      if (mightBeVideoRequest && videoFiles.length > 0) {
        console.log('🎬 Potential video analysis request detected (lenient check)');
        // Trigger video analysis instead of sending chat
        const userMessage: Message = {
          id: Date.now().toString(),
          role: 'user',
          content: messageContent,
          timestamp: new Date(),
          mediaUrls: mediaToSend
        };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        
        const videoUrls = pendingMediaUrls.filter(url => /\.(mp4|mov|avi|webm|mkv)$/i.test(url));
        console.log('🎬 [Video Analysis] Matching video files to URLs:', {
          videoFileCount: videoFiles.length,
          videoUrlCount: videoUrls.length,
          videoFiles: videoFiles.map(f => ({ 
            name: f.name, 
            size: f.size,
            sizeMB: `${(f.size / 1024 / 1024).toFixed(2)}MB`,
            hasData: !!f.data,
            dataLength: f.data?.length || 0
          })),
          videoUrls: videoUrls,
          allPendingUrls: pendingMediaUrls
        });
        
        for (let i = 0; i < videoFiles.length; i++) {
          const videoFile = videoFiles[i];
          
          // CRITICAL: For videos, we MUST use the uploaded URL if available
          // Base64 conversion for large videos fails in production due to memory limits
          let videoUrl = videoUrls[i];
          
          // Try multiple matching strategies
          if (!videoUrl) {
            // Strategy 1: Match by index
            videoUrl = videoUrls[i];
          }
          
          if (!videoUrl) {
            // Strategy 2: Match by filename in URL
            const fileNameMatch = videoFile.name.toLowerCase().replace(/[^a-z0-9]/g, '');
            const foundUrl2 = videoUrls.find(url => {
              if (!url) return false;
              const urlMatch = url.toLowerCase().replace(/[^a-z0-9]/g, '');
              return urlMatch.includes(fileNameMatch) || fileNameMatch.includes(urlMatch);
            });
            if (foundUrl2) videoUrl = foundUrl2;
          }
          
          if (!videoUrl) {
            // Strategy 3: Match by encoded filename
            const foundUrl3 = videoUrls.find(url => {
              if (!url) return false;
              return url.includes(videoFile.name) || 
                     url.includes(encodeURIComponent(videoFile.name)) ||
                     url.includes(encodeURIComponent(videoFile.name.replace(/\s/g, '%20')));
            });
            if (foundUrl3) videoUrl = foundUrl3;
          }
          
          // Strategy 4: If we have any video URL and only one video file, use it
          if (!videoUrl && videoUrls.length === 1 && videoFiles.length === 1) {
            videoUrl = videoUrls[0];
            console.log('🎯 [Video Analysis] Using single available video URL (1:1 match)');
          }
          
          if (videoUrl) {
            // Convert relative URL to absolute if needed
            const absoluteUrl = videoUrl.startsWith('http') 
              ? videoUrl 
              : `${API_BASE_URL}${videoUrl.startsWith('/') ? '' : '/'}${videoUrl}`;
            
            console.log(`✅ [Video Analysis] Using uploaded URL for ${videoFile.name}:`, {
              originalUrl: videoUrl,
              absoluteUrl: absoluteUrl,
              fileSize: `${(videoFile.size / 1024 / 1024).toFixed(2)}MB`
            });
            await handleVideoAnalysis(videoFile, absoluteUrl);
          } else {
            // CRITICAL: Don't fall back to base64 for large videos - it fails in production
            if (videoFile.size > 5 * 1024 * 1024) { // 5MB threshold
              console.error('❌ [Video Analysis] No URL found and file is too large for base64:', {
                fileName: videoFile.name,
                size: `${(videoFile.size / 1024 / 1024).toFixed(2)}MB`,
                availableUrls: videoUrls,
                recommendation: 'Video must be uploaded first via analyze-media to get a URL'
              });
              throw new Error(`Video file "${videoFile.name}" is too large (${(videoFile.size / 1024 / 1024).toFixed(2)}MB) to convert from base64. Please ensure the video was uploaded successfully first.`);
            }
            
            console.warn(`⚠️ [Video Analysis] No URL found for ${videoFile.name}, attempting base64 conversion (risky for large files)`);
            await handleVideoAnalysis(videoFile);
          }
        }
        
        setPendingMediaFiles([]);
        setPendingMediaUrls([]);
        return;
      }
    }

    // Extract document texts and images from pending files
    let documentTexts: Array<{ name: string; text: string; images?: Array<{ id: string; url: string; name: string }> }> = [];
    const documentFiles = pendingMediaFiles.filter(f => 
      f.type === 'application/pdf' || 
      f.name.endsWith('.docx') || 
      f.name.endsWith('.doc') || 
      f.name.endsWith('.pptx') || 
      f.name.endsWith('.ppt')
    );

    // If we have document files, extract their text and images via backend
    if (documentFiles.length > 0) {
      // Remove documents from pending attachments since they're being processed
      const documentNames = documentFiles.map(f => f.name);
      setPendingMediaFiles(prev => prev.filter(f => !documentNames.includes(f.name)));
      const documentUrls = pendingMediaUrls.filter(url => 
        documentFiles.some(f => url.includes(f.name))
      );
      setPendingMediaUrls(prev => prev.filter(url => !documentUrls.includes(url)));
      
      try {
        const formData = new FormData();
        for (const mediaFile of documentFiles) {
          const byteCharacters = atob(mediaFile.data.split(',')[1] || mediaFile.data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mediaFile.type });
          const file = new File([blob], mediaFile.name, { type: mediaFile.type });
          formData.append('files', file);
        }

        const extractResponse = await fetch(`${API_BASE_URL}/ai-assistant/extract-documents`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getValidToken() || ''}`
          },
          body: formData
        });

        if (extractResponse.ok) {
          const extractData = await extractResponse.json();
          documentTexts = extractData.documentTexts || [];
          
          // Add extracted images to pending media URLs so they can be used by AI
          if (extractData.images && extractData.images.length > 0) {
            const imageUrls = extractData.images.map((img: { url: string }) => img.url);
            setPendingMediaUrls(prev => [...prev, ...imageUrls]);
            console.log(`Added ${imageUrls.length} extracted images from documents to media URLs`);
          }
        }
      } catch (error) {
        console.error('Error extracting document content:', error);
        // Continue without document texts
      }
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageContent,
      timestamp: new Date(),
      mediaUrls: mediaToSend
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    // Clear pending media after sending
    setPendingMediaFiles([]);
    setPendingMediaUrls([]);
    setMediaPlacement(null);
    setShowMediaPlacementModal(false);
    setIsLoading(true);

    // Create assistant message placeholder
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, assistantMessage]);

    // Abort any previous requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(`${API_BASE_URL}/ai-assistant/chat-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getValidToken() || ''}`
        },
        body: JSON.stringify({
          message: messageContent + (placementInfo ? `\n\n[Media Placement: Insert ${placementInfo.position === 'before' ? 'before' : placementInfo.position === 'after' ? 'after' : placementInfo.position === 'within' ? 'within' : 'as a new step'} step "${placementInfo.stepId || 'selected'}"].` : ''),
          mediaUrls: mediaToSend || [],
          currentTutorial: tutorialState,
          conversationHistory: getConversationContext(messages, 20).map(m => ({
            role: m.role,
            content: m.content
          })),
          documentTexts: documentTexts,
          mediaPlacement: placementInfo
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        // Check if it's an authentication error
        if (response.status === 401 || response.status === 403) {
          await response.json().catch(() => ({ error: 'Authentication failed' }));
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token'); // Remove both for compatibility
          localStorage.removeItem('user');
          toast.error('Your session has expired. Please log in again.');
          window.location.href = '/login';
          return;
        }
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to get AI response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'token' && data.token) {
                  setMessages(prev => prev.map(msg => 
                    msg.id === assistantMessageId 
                      ? { ...msg, content: msg.content + data.token }
                      : msg
                  ));
                } else if (data.type === 'update' && data.tutorialUpdate) {
                  const previousSteps = tutorialState.steps.map(s => s.id);
                  
                  // Clean tutorial update to remove any raw JSON dumps
                  const cleanUpdate = {
                    ...data.tutorialUpdate,
                    description: data.tutorialUpdate.description ? 
                      data.tutorialUpdate.description.replace(/\*?STEPS_FROM_VIDEO_ANALYSIS[^*]*\*/g, '')
                        .replace(/\*?StepsFromVideoAnalysis\s*:\s*\[[^\]]*\][^*]*/g, '')
                        .replace(/\{[^}]*"title"[^}]*"description"[^}]*\}[^}]*/g, '')
                        .trim() 
                      : data.tutorialUpdate.description
                  };
                  
                  // Ensure all steps have IDs - fix missing IDs from AI responses
                  // Also clean step content to remove raw JSON dumps
                  const updatedSteps = cleanUpdate.steps?.map((step: TutorialStep, index: number) => {
                    const cleanedStep = {
                      ...step,
                      content: step.content ? 
                        step.content.replace(/\*?STEPS_FROM_VIDEO_ANALYSIS[^*]*\*/g, '')
                          .replace(/\*?StepsFromVideoAnalysis\s*:\s*\[[^\]]*\][^*]*/g, '')
                          .replace(/\{title:\s*"[^"]*",\s*description:\s*"[^"]*",\s*prompt:\s*"[^"]*"[^}]*\}/g, '')
                          .trim()
                        : step.content
                    };
                    
                    if (!cleanedStep.id) {
                      // Generate ID for steps missing it
                      const newId = `step-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;
                      console.warn('Step missing ID, generated:', newId, 'for step:', cleanedStep.title);
                      return { ...cleanedStep, id: newId };
                    }
                    return cleanedStep;
                  }) || [];
                  
                  const newState = { 
                    ...tutorialState, 
                    ...cleanUpdate,
                    steps: updatedSteps
                  };
                  
                  // Track newly added steps
                  const newSteps = newState.steps?.filter((s: TutorialStep) => !previousSteps.includes(s.id)) || [];
                  const modifiedSteps = newState.steps?.filter((s: TutorialStep) => {
                    const oldStep = tutorialState.steps.find(os => os.id === s.id);
                    return oldStep && (oldStep.content !== s.content || oldStep.title !== s.title);
                  }) || [];
                  
                  if (newSteps.length > 0 || modifiedSteps.length > 0) {
                    // Update message with created/updated step IDs
                    // Ensure we get unique IDs and preserve order (use filter to remove duplicates while preserving order)
                    const createdIds = newSteps
                      .map((s: TutorialStep) => s.id)
                      .filter((id: string, index: number, self: string[]) => id && self.indexOf(id) === index); // Remove duplicates, preserve order
                    const updatedIds = modifiedSteps
                      .map((s: TutorialStep) => s.id)
                      .filter((id: string, index: number, self: string[]) => id && self.indexOf(id) === index);
                    
                    console.log('Tracking created steps:', createdIds, 'with titles:', newSteps.map((s: TutorialStep) => s.title));
                    console.log('Tracking updated steps:', updatedIds);
                    
                    setMessages(prev => prev.map(msg =>
                      msg.id === assistantMessageId
                        ? { ...msg, createdSteps: createdIds, updatedSteps: updatedIds }
                        : msg
                    ));
                    
                    // Track for animations
                    if (createdIds.length > 0) {
                      setNewlyAddedSteps(new Set(createdIds));
                      setTimeout(() => setNewlyAddedSteps(new Set()), 3000);
                    }
                    if (updatedIds.length > 0) {
                      setUpdatedSteps(new Set(updatedIds));
                      setTimeout(() => setUpdatedSteps(new Set()), 2000);
                    }
                  }
                  
                  setTutorialState(newState);
                  addToHistory(newState);
                } else if (data.type === 'glossary' && data.addedCount > 0) {
                  // Add a notification message about glossary keywords
                  const glossaryMessage: Message = {
                    id: `glossary-${Date.now()}`,
                    role: 'assistant',
                    content: `📚 **Glossary Update:** I've automatically extracted ${data.addedCount} technical ${data.addedCount === 1 ? 'term' : 'terms'} from this tutorial and added ${data.addedCount === 1 ? 'it' : 'them'} to the glossary. ${data.keywords && data.keywords.length > 0 ? `Key terms added: ${data.keywords.slice(0, 5).map((k: any) => `**${k.term}**`).join(', ')}${data.addedCount > 5 ? ' and more' : ''}.` : ''} These terms will now be highlighted in tutorials to help learners understand technical terminology.`,
                    timestamp: new Date()
                  };
                  setMessages(prev => [...prev, glossaryMessage]);
                  toast.success(`Added ${data.addedCount} technical ${data.addedCount === 1 ? 'term' : 'terms'} to glossary`, { 
                    icon: '📚',
                    duration: 5000 
                  });
                } else if (data.type === 'error') {
                  throw new Error(data.error);
                } else if (data.type === 'done') {
                  // Finalize message
                  autoSaveDraft(tutorialState, messages);
                }
              } catch (parseError) {
                console.error('Failed to parse SSE data:', parseError);
              }
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Error sending message:', error);
      toast.error('Failed to get AI response. Please try again.');
      
      // Remove the incomplete assistant message
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again or rephrase your request.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  // Video analysis function - kept for potential future use if user wants explicit analysis button
  // Currently videos are analyzed on-demand when user asks in chat
  const handleVideoAnalysis = async (videoFile: MediaFile, videoUrl?: string) => {
    try {
      // Remove the video from pending attachments since it's being processed
      setPendingMediaFiles(prev => prev.filter(f => f.name !== videoFile.name));
      setPendingMediaUrls(prev => prev.filter(url => url !== videoUrl));
      
      // Add a chat message to indicate video analysis is in progress
      const loadingMessage: Message = {
        id: `video-analysis-loading-${Date.now()}`,
        role: 'assistant',
        content: `🎥 Analyzing video "${videoFile.name}" with AI... This may take a moment.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, loadingMessage]);
      
      let file: File;
      
      if (videoUrl) {
        // Video is already on server - fetch it
        console.log('📥 [Video Analysis] Fetching video from server URL:', videoUrl);
        try {
          const videoResponse = await fetch(videoUrl);
          if (!videoResponse.ok) {
            throw new Error(`Failed to fetch video from server: ${videoResponse.status} ${videoResponse.statusText}`);
          }
          const videoBlob = await videoResponse.blob();
          console.log('✅ [Video Analysis] Video blob fetched:', {
            blobSize: `${(videoBlob.size / 1024 / 1024).toFixed(2)}MB`,
            blobType: videoBlob.type
          });
          file = new File([videoBlob], videoFile.name, { type: videoFile.type });
          console.log('✅ [Video Analysis] File created from blob:', {
            fileName: file.name,
            fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
            fileType: file.type
          });
        } catch (fetchError: any) {
          console.error('❌ [Video Analysis] Failed to fetch video from URL:', fetchError);
          throw new Error(`Failed to fetch video from server: ${fetchError.message}. Please try uploading the video again.`);
        }
      } else {
        // Convert MediaFile to File (fallback for base64)
        // WARNING: Base64 conversion for large videos can fail or create empty files
        console.warn('⚠️ [Video Analysis] No video URL available, attempting base64 conversion (may fail for large files)');
        console.log('📥 Converting base64 video to File...');
        console.log('📊 [Video Analysis] Base64 data info:', {
          hasData: !!videoFile.data,
          dataLength: videoFile.data?.length || 0,
          dataPreview: videoFile.data?.substring(0, 50) || 'no data',
          originalSize: videoFile.size,
          originalSizeMB: `${(videoFile.size / 1024 / 1024).toFixed(2)}MB`
        });
        
        // Warn if file is large - base64 conversion often fails for videos > 10MB
        if (videoFile.size > 10 * 1024 * 1024) {
          console.error('❌ [Video Analysis] Video file is too large for base64 conversion:', {
            size: `${(videoFile.size / 1024 / 1024).toFixed(2)}MB`,
            recommendation: 'Video should be uploaded via analyze-media first to get a URL'
          });
          throw new Error(`Video file is too large (${(videoFile.size / 1024 / 1024).toFixed(2)}MB) for base64 conversion. Please upload the video first, then analyze it.`);
        }
        
        if (!videoFile.data || videoFile.data.length === 0) {
          throw new Error('Video file data is missing. Please re-upload the video file.');
        }
        
        // Extract base64 data (remove data URL prefix if present)
        const base64Data = videoFile.data.includes(',') 
          ? videoFile.data.split(',')[1] 
          : videoFile.data;
        
        if (!base64Data || base64Data.length === 0) {
          throw new Error('Invalid video file data. Please re-upload the video file.');
        }
        
        try {
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: videoFile.type });
          file = new File([blob], videoFile.name, { type: videoFile.type });
          
          // CRITICAL: Verify the File object actually has data
          if (file.size === 0) {
            console.error('❌ [Video Analysis] File object created but is empty!', {
              blobSize: blob.size,
              fileSize: file.size,
              originalSize: videoFile.size,
              base64Length: base64Data.length
            });
            throw new Error('Failed to convert video file. The file may be too large. Please try uploading the video directly or use a smaller file.');
          }
          
          console.log('✅ [Video Analysis] File conversion successful:', {
            fileSize: file.size,
            blobSize: blob.size,
            matchesOriginal: file.size === videoFile.size
          });
        } catch (conversionError: any) {
          console.error('❌ [Video Analysis] Base64 conversion failed:', conversionError);
          throw new Error(`Failed to process video file: ${conversionError.message}. Please try uploading the video again.`);
        }
      }
      
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
      console.log('📤 [Video Analysis] Starting upload:', {
        fileName: file.name,
        fileSize: `${fileSizeMB}MB`,
        fileSizeBytes: file.size,
        fileType: file.type,
        lastModified: file.lastModified ? new Date(file.lastModified).toISOString() : 'unknown',
        timestamp: new Date().toISOString()
      });
      
      // CRITICAL: Validate file size before upload
      if (file.size === 0) {
        console.error('❌ [Video Analysis] File is empty (0 bytes) - cannot upload');
        console.error('📊 [Video Analysis] File details:', {
          name: file.name,
          type: file.type,
          size: file.size,
          lastModified: file.lastModified,
          hasVideoUrl: !!videoUrl,
          videoFileOriginalSize: videoFile.size
        });
        throw new Error('The video file is empty (0 bytes). This may happen if the file is too large for base64 conversion. Please try uploading the video directly or use a smaller file.');
      }
      
      // Additional validation: Check if file size matches original
      if (videoFile.size > 0 && file.size !== videoFile.size) {
        console.warn('⚠️ [Video Analysis] File size mismatch:', {
          originalSize: videoFile.size,
          convertedSize: file.size,
          difference: `${((file.size - videoFile.size) / 1024 / 1024).toFixed(2)}MB`
        });
        // Don't throw error, but log warning - sometimes there can be small differences
      }
      
      // For video analysis, skip frontend token validation - let backend handle it
      // This allows long-running operations to complete even if token expires during processing
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
      console.log('🔑 [Video Analysis] Token status:', token ? 'Token present' : 'No token');
      
      const formData = new FormData();
      formData.append('video', file);
      
      // Verify FormData contains the file and check its size
      const formDataFile = formData.get('video') as File | null;
      console.log('📦 [Video Analysis] FormData prepared:', {
        hasFile: formData.has('video'),
        fileInFormData: formDataFile ? {
          name: formDataFile.name,
          size: formDataFile.size,
          type: formDataFile.type
        } : 'null',
        apiUrl: `${API_BASE_URL}/ai-assistant/analyze-video`
      });
      
      // Final check: Ensure the file in FormData is not empty
      if (formDataFile && formDataFile.size === 0) {
        console.error('❌ [Video Analysis] File in FormData is empty!');
        throw new Error('Failed to prepare video file for upload. The file appears to be empty. Please try uploading again.');
      }
      
      const uploadStartTime = Date.now();
      console.log('⏱️ [Video Analysis] Upload started at:', new Date().toISOString());
      
      // Create AbortController with 5 minute timeout for video analysis
      // Video analysis can take time: AI processing (~20s) + frame extraction (~30-60s)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn('⏱️ [Video Analysis] Request timeout after 5 minutes');
        controller.abort();
      }, 5 * 60 * 1000); // 5 minutes
      
      let response: Response;
      try {
        response = await fetch(`${API_BASE_URL}/ai-assistant/analyze-video`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: formData,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Video analysis timed out. The video may be too long or complex. Please try with a shorter video or contact support.');
        }
        throw fetchError;
      }
      
      const uploadEndTime = Date.now();
      const uploadDuration = ((uploadEndTime - uploadStartTime) / 1000).toFixed(2);
      console.log(`✅ [Video Analysis] Upload completed in ${uploadDuration}s, status: ${response.status}`);
      
      if (!response.ok) {
        // Check if it's an authentication error
        if (response.status === 401 || response.status === 403) {
          await response.json().catch(() => ({ error: 'Authentication failed' }));
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token'); // Remove both for compatibility
          localStorage.removeItem('user');
          
          // Remove loading message and show authentication error in chat
          const authErrorMessage: Message = {
            id: Date.now().toString(),
            role: 'assistant',
            content: `❌ Your session has expired. Please log in again to continue analyzing the video.`,
            timestamp: new Date()
          };
          setMessages(prev => [
            ...prev.filter(msg => !msg.id.startsWith('video-analysis-loading-')),
            authErrorMessage
          ]);
          
          toast.error('Your session has expired. Please log in again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }
        
        const errorData = await response.json().catch(() => ({ error: 'Video analysis failed' }));
        throw new Error(errorData.error || 'Failed to analyze video');
      }
      
      console.log('📥 [Video Analysis] Parsing response...');
      const data = await response.json();
      console.log('📊 [Video Analysis] Response received:', {
        success: data.success,
        hasTutorial: !!data.tutorial,
        stepCount: data.tutorial?.steps?.length || 0,
        frameCount: data.extractedFrames?.length || 0,
        hasAnalysis: !!data.analysis
      });
      
      if (data.success && data.tutorial) {
        console.log('✨ [Video Analysis] Processing successful tutorial data...');
        // Clean tutorial data to ensure no raw JSON is included
        // IMPORTANT: Preserve image tags when cleaning - they contain extracted frames
        const cleanTutorial = {
          ...data.tutorial,
          description: data.tutorial.description ? 
            data.tutorial.description.replace(/\*?STEPS_FROM_VIDEO_ANALYSIS[^*]*\*/g, '')
              .replace(/\*?StepsFromVideoAnalysis\s*:\s*\[[^\]]*\][^*]*/g, '')
              .replace(/\{[^}]*"title"[^}]*"description"[^}]*\}[^}]*/g, '')
              .trim() 
            : data.tutorial.description,
          steps: data.tutorial.steps?.map((step: any) => {
            // Clean the content
            let cleanedContent = step.content ? 
              step.content.replace(/\*?STEPS_FROM_VIDEO_ANALYSIS[^*]*\*/g, '')
                .replace(/\*?StepsFromVideoAnalysis\s*:\s*\[[^\]]*\][^*]*/g, '')
                .replace(/\{title:\s*"[^"]*",\s*description:\s*"[^"]*",\s*prompt:\s*"[^"]*"[^}]*\}/g, '')
                .trim()
              : step.content;
            
            // Frames are automatically assigned based on AI-estimated timestamps
            // Users can still manually override using the FrameSelector UI (green image icon button)
            
            return {
              ...step,
              content: cleanedContent
            };
          }) || []
        };

        // Store extracted frames with metadata
        if (data.extractedFrames && data.extractedFrames.length > 0) {
          // Normalize frames - handle both old format (strings) and new format (objects)
          const normalizedFrames = data.extractedFrames.map((frame: any) => {
            if (typeof frame === 'string') {
              // Old format - create object with estimated timestamp
              return {
                url: frame,
                timestamp: 0, // Unknown timestamp for old format
                timeString: '0:00'
              };
            }
            return frame; // New format already has url, timestamp, timeString
          });
          setExtractedFrames(normalizedFrames);
        }
        
        // Update tutorial state with cleaned content
        setTutorialState({
          ...tutorialState,
          title: cleanTutorial.title || tutorialState.title,
          description: cleanTutorial.description || tutorialState.description,
          estimatedTime: cleanTutorial.estimatedTime || tutorialState.estimatedTime,
          difficulty: cleanTutorial.difficulty || tutorialState.difficulty,
          category: cleanTutorial.category || tutorialState.category,
          steps: cleanTutorial.steps || tutorialState.steps
        });
        
        // Remove the loading message and add the final result message
        const frameCount = data.extractedFrames?.length || 0;
        const frameMessage = frameCount > 0 
          ? ` I've also extracted ${frameCount} screenshot${frameCount > 1 ? 's' : ''} from your video and automatically matched them to steps based on when each step occurs. You can use the green image icon (📷) next to any step to change or add screenshots.`
          : '';
        
        // Use the clean analysis message from backend, or generate a user-friendly one
        const analysisContent = data.analysis || 
          `✅ I've analyzed your video "${videoFile.name}" and generated a tutorial structure with ${cleanTutorial.steps?.length || 0} steps.${frameMessage} Review the steps in the sidebar and let me know if you'd like any adjustments!`;
        
        const analysisMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: analysisContent,
          timestamp: new Date(),
          createdSteps: cleanTutorial.steps?.map((s: any) => s.id) || []
        };
        
        // Remove loading message and add result message
        setMessages(prev => [
          ...prev.filter(msg => !msg.id.startsWith('video-analysis-loading-')),
          analysisMessage
        ]);
        
        const totalTime = ((Date.now() - uploadStartTime) / 1000).toFixed(2);
        console.log(`🎉 [Video Analysis] Complete! Total time: ${totalTime}s, Steps: ${cleanTutorial.steps?.length || 0}, Frames: ${frameCount}`);
      } else {
        // Remove loading message and show warning in chat
        const warningMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `⚠️ ${data.warning || 'Video analysis completed but AI analysis was unavailable. You can still describe the video content manually.'}`,
          timestamp: new Date()
        };
        setMessages(prev => [
          ...prev.filter(msg => !msg.id.startsWith('video-analysis-loading-')),
          warningMessage
        ]);
      }
    } catch (error: any) {
      console.error('❌ [Video Analysis] Error occurred:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      // Remove loading message and show error in chat
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `❌ Failed to analyze video: ${error.message || 'An unexpected error occurred. Please try again.'}`,
        timestamp: new Date()
      };
      setMessages(prev => [
        ...prev.filter(msg => !msg.id.startsWith('video-analysis-loading-')),
        errorMessage
      ]);
    }
  };

  // Validate file (similar to MediaUploader)
  const validateFile = (file: File): string | null => {
    const maxSize = 12; // 12MB
    const acceptedTypes = [
      'image/*', 
      'video/*', 
      'application/pdf', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', 
      'application/vnd.ms-powerpoint'
    ];

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `File size must be less than ${maxSize}MB`;
    }

    // Check file type - including extensions for Office docs
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const isValidExtension = fileExtension && ['doc', 'docx', 'ppt', 'pptx', 'pdf'].includes(fileExtension);
    
    const isValidType = acceptedTypes.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.slice(0, -1));
      }
      return file.type === type;
    });

    if (!isValidType && !isValidExtension) {
      return `File type ${file.type || fileExtension} is not supported`;
    }

    return null;
  };

  // Helper function to convert File to MediaFile
  const convertFileToMediaFile = async (file: File): Promise<MediaFile> => {
    // Compress images if they're larger than 1MB
    let processedFile = file;
    if (file.type.startsWith('image/') && file.size > 1024 * 1024) {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        await new Promise((resolve, reject) => {
          img.onload = () => {
            const maxWidth = 1920;
            const maxHeight = 1080;
            let width = img.width;
            let height = img.height;
            
            if (width > maxWidth || height > maxHeight) {
              if (width > height) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              } else {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            ctx?.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob((blob) => {
              if (blob) {
                processedFile = new File([blob], file.name, { type: file.type });
              }
              resolve(null);
            }, file.type, 0.8);
          };
          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });
      } catch (error) {
        console.warn('Failed to compress image, using original:', error);
      }
    }
    
    // Convert to base64
    const convertToBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };
    
    const data = await convertToBase64(processedFile);
    return {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      name: processedFile.name,
      type: processedFile.type,
      size: processedFile.size,
      data,
      uploadedAt: Date.now()
    };
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handlePastedFiles = async (files: File[]) => {
    if (files.length === 0) return;

    // Validate files
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      toast.error(errors.join('\n'));
    }

    if (validFiles.length === 0) return;

    // Check max files limit
    if (pendingMediaFiles.length + validFiles.length > 30) {
      toast.error(`Maximum 30 files allowed. You already have ${pendingMediaFiles.length} file(s).`);
      return;
    }

    try {
      toast.loading(`Processing ${validFiles.length} file(s)...`, { id: 'paste-upload' });
      
      // Convert files to MediaFile format
      const mediaFiles: MediaFile[] = [];
      for (const file of validFiles) {
        try {
          const mediaFile = await convertFileToMediaFile(file);
          mediaFiles.push(mediaFile);
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
          toast.error(`Failed to process ${file.name}`);
        }
      }

      if (mediaFiles.length > 0) {
        await handleMediaUpload(mediaFiles);
        toast.dismiss('paste-upload');
      } else {
        toast.dismiss('paste-upload');
        toast.error('No valid files to upload');
      }
    } catch (error) {
      console.error('Error handling pasted files:', error);
      toast.dismiss('paste-upload');
      toast.error('Failed to process pasted files');
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handlePastedFiles(files);
    }
  };

  const handleMediaUpload = async (files: MediaFile[]) => {
    // Upload all files (including videos) as attachments - wait for user instructions
    // Videos will be stored as pending attachments until user explicitly asks to analyze them
    await uploadNonVideoFiles(files);
  };

  const uploadNonVideoFiles = async (files: MediaFile[]) => {
    // Close modal immediately - don't block user
    setShowMediaUpload(false);
    
    if (files.length === 0) return;
    
    toast.loading(`Uploading ${files.length} file(s)...`, { id: 'media-upload' });
    
    try {
      const formData = new FormData();
      
      for (const mediaFile of files) {
        const byteCharacters = atob(mediaFile.data.split(',')[1] || mediaFile.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mediaFile.type });
        const file = new File([blob], mediaFile.name, { type: mediaFile.type });
        formData.append('files', file);
      }

      toast.loading(`Uploading ${files.length} file(s) to server...`, { id: 'media-upload' });
      
      // Upload WITHOUT AI analysis (fast path) - add ?analyze=true if you want AI analysis
      const response = await fetch(`${API_BASE_URL}/ai-assistant/analyze-media?analyze=false`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getValidToken() || ''}`
        },
        body: formData
      });

      if (!response.ok) {
        // Check if it's an authentication error
        if (response.status === 401 || response.status === 403) {
          await response.json().catch(() => ({ error: 'Authentication failed' }));
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token'); // Remove both for compatibility
          localStorage.removeItem('user');
          toast.error('Your session has expired. Please log in again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }
        
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorData.error || 'Failed to upload media');
      }

      const data = await response.json();
      const mediaUrls = data.mediaUrls || [];
      const documentTexts = data.documentTexts || []; // Extract document texts if available
      
      // Store the uploaded files and URLs for the user to reference
      setPendingMediaFiles([...pendingMediaFiles, ...files]);
      setPendingMediaUrls([...pendingMediaUrls, ...mediaUrls]);
      
      // Store document texts for sending to AI
      if (documentTexts.length > 0) {
        // Store in a way that we can send with the message
        (pendingMediaFiles as any).documentTexts = documentTexts;
      }
      
      // Check file types for appropriate messaging
      const videoFiles = files.filter(f => f.type.startsWith('video/'));
      const docFiles = files.filter(f => 
        f.type === 'application/pdf' || 
        f.name.endsWith('.docx') || 
        f.name.endsWith('.doc') || 
        f.name.endsWith('.pptx') || 
        f.name.endsWith('.ppt')
      );
      const imageFiles = files.filter(f => 
        f.type.startsWith('image/') && 
        !f.type.includes('pdf') && 
        !f.name.endsWith('.docx') && 
        !f.name.endsWith('.doc') && 
        !f.name.endsWith('.pptx') && 
        !f.name.endsWith('.ppt')
      );
      
      toast.dismiss('media-upload');
      
      // Show placement modal for images/videos if steps exist
      const nonDocFiles = files.filter(f => 
        !f.type.includes('pdf') && 
        !f.name.endsWith('.docx') && 
        !f.name.endsWith('.doc') && 
        !f.name.endsWith('.pptx') && 
        !f.name.endsWith('.ppt')
      );
      
      if (nonDocFiles.length > 0 && tutorialState.steps.length > 0) {
        // Show placement modal for images/videos (non-blocking)
        toast.success(`${files.length} file(s) uploaded successfully! Choose where to place them.`, { duration: 4000 });
        // Small delay to let modal close animation finish
        setTimeout(() => {
          setShowMediaPlacementModal(true);
        }, 300);
      } else {
        // Provide specific feedback based on file types
        let message = '';
        if (videoFiles.length > 0) {
          message = `${videoFiles.length} video file(s) uploaded as attachment${videoFiles.length > 1 ? 's' : ''}. `;
          if (docFiles.length > 0) {
            message += `${docFiles.length} document(s) will be analyzed when you send your message. `;
          }
          if (imageFiles.length > 0) {
            message += `${imageFiles.length} image(s) ready. `;
          }
          message += `Tell me what you'd like to do with ${videoFiles.length === 1 ? 'the video' : 'the videos'} in your message.`;
        } else if (docFiles.length > 0) {
          message = `${files.length} file(s) uploaded. ${docFiles.length} document(s) will be analyzed when you send your message.`;
        } else {
          message = `${files.length} file(s) uploaded. Describe what you'd like to do with ${files.length === 1 ? 'it' : 'them'} in your message.`;
        }
        toast.success(message, { duration: 5000 });
      }
    } catch (error: any) {
      console.error('Error uploading media:', error);
      toast.dismiss('media-upload');
      toast.error(error.message || 'Failed to upload media. Please try again.');
    }
  };

  // Debounced manual edit
  const debouncedEdit = useMemo(
    () => debounce((field: keyof TutorialState, value: any) => {
      const newState = { ...tutorialState, [field]: value };
      addToHistory(newState);
      autoSaveDraft(newState, messages);
    }, 500),
    [tutorialState, messages, addToHistory, autoSaveDraft]
  );

  const handleManualEdit = (field: keyof TutorialState, value: any) => {
    setTutorialState(prev => ({ ...prev, [field]: value }));
    debouncedEdit(field, value);
  };

  const handleStepEdit = (stepId: string, field: 'title' | 'content', value: string) => {
    setTutorialState(prev => {
      const updatedSteps = prev.steps.map(step =>
        step.id === stepId ? { ...step, [field]: value } : step
      );
      const newState = { ...prev, steps: updatedSteps };
      addToHistory(newState);
      autoSaveDraft(newState, messages);
      return newState;
    });
  };

  // Extract frames from all step content
  const extractFramesFromSteps = useCallback(() => {
    const allFrameUrls = new Set<string>();
    
    // Collect all frame URLs from all steps
    tutorialState.steps.forEach(step => {
      if (!step.content) return;
      
      const imgMatches = step.content.match(/<img[^>]+src="([^"]+)"/gi);
      if (!imgMatches) return;
      
      imgMatches.forEach(match => {
        const srcMatch = match.match(/src="([^"]+)"/);
        if (srcMatch && srcMatch[1] && srcMatch[1].includes('/uploads/frames/')) {
          allFrameUrls.add(srcMatch[1]);
        }
      });
    });
    
    // Convert to frame objects
    // Note: Frames use UUID filenames, so we can't extract timestamps from filenames
    // We'll use index-based ordering and show "N/A" for timestamps
    return Array.from(allFrameUrls).map((url, index) => {
      // Since we can't extract timestamp from UUID filename, use index as a fallback
      // This ensures frames are displayed even if timestamp info is lost
      return {
        url,
        timestamp: index, // Use index as a simple ordering mechanism
        timeString: 'N/A' // Show "N/A" since we don't have real timestamp info
      };
    });
  }, [tutorialState.steps]);

  // Handler to open frame selector for a step
  const handleOpenFrameSelector = (stepId: string) => {
    // If extractedFrames is empty, try to extract from step content
    if (extractedFrames.length === 0) {
      const framesFromSteps = extractFramesFromSteps();
      if (framesFromSteps.length > 0) {
        setExtractedFrames(framesFromSteps);
      }
    }
    
    setFrameSelectorStepId(stepId);
    setFrameSelectorOpen(true);
  };

  // Handler to update step content with selected frames
  const handleSelectFrames = (stepId: string, frameUrls: string[]) => {
    setTutorialState(prev => {
      const step = prev.steps.find(s => s.id === stepId);
      if (!step) return prev;

      // Remove existing images from step content
      let cleanedContent = step.content || '';
      cleanedContent = cleanedContent.replace(/<img[^>]*>/gi, '').trim();

      // Add selected frames as images
      if (frameUrls.length > 0) {
        const imageHtml = frameUrls.map((url, idx) => 
          `<img src="${url}" alt="Screenshot ${idx + 1}" class="max-w-full h-auto rounded-lg my-4" />`
        ).join('\n');
        
        cleanedContent = cleanedContent 
          ? `${imageHtml}\n\n${cleanedContent}`
          : imageHtml;
      }

      const updatedSteps = prev.steps.map(s =>
        s.id === stepId ? { ...s, content: cleanedContent } : s
      );
      
      const newState = { ...prev, steps: updatedSteps };
      addToHistory(newState);
      autoSaveDraft(newState, messages);
      return newState;
    });
    
    toast.success(`Added ${frameUrls.length} screenshot${frameUrls.length > 1 ? 's' : ''} to step`);
  };

  // Get current frames in a step
  const getCurrentFramesInStep = (stepId: string): string[] => {
    const step = tutorialState.steps.find(s => s.id === stepId);
    if (!step || !step.content) return [];
    
    const imgMatches = step.content.match(/<img[^>]+src="([^"]+)"/gi);
    if (!imgMatches) return [];
    
    return imgMatches.map(match => {
      const srcMatch = match.match(/src="([^"]+)"/);
      return srcMatch ? srcMatch[1] : '';
    }).filter(url => url && url.includes('/uploads/frames/'));
  };

  // Check if there are any extracted frames available (either in state or in step content)
  const hasAvailableFrames = useMemo(() => {
    if (extractedFrames.length > 0) return true;
    
    // Check if any step has frames in its content
    return tutorialState.steps.some(step => {
      if (!step.content) return false;
      return step.content.includes('/uploads/frames/');
    });
  }, [extractedFrames, tutorialState.steps]);

  const handleGenerateStepContent = async (stepId: string, silent = false) => {
    const step = tutorialState.steps.find(s => s.id === stepId);
    if (!step) return;

    setFocusedStepId(stepId);
    
    if (silent) {
      // Direct generation without chat message
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/ai-assistant/chat-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getValidToken() || ''}`
          },
          body: JSON.stringify({
            message: `Generate detailed content for this tutorial step. Title: "${step.title}". Context: This is part of a tutorial titled "${tutorialState.title}". ${tutorialState.description ? `Description: ${tutorialState.description}` : ''}\n\nCreate comprehensive, well-structured content for this step. Use markdown formatting where appropriate.`,
            currentTutorial: tutorialState,
            conversationHistory: []
          }),
          signal: abortControllerRef.current?.signal || undefined
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            toast.error('Your session has expired. Please log in again.');
            window.location.href = '/login';
            return;
          }
          throw new Error('Failed to generate content');
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let fullResponse = '';
        let tutorialUpdate: any = null;

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.type === 'token' && data.token) {
                    fullResponse += data.token;
                  } else if (data.type === 'update' && data.tutorialUpdate) {
                    tutorialUpdate = data.tutorialUpdate;
                  } else if (data.type === 'done') {
                    if (tutorialUpdate) {
                      const newState = { ...tutorialState, ...tutorialUpdate };
                      setTutorialState(newState);
                      addToHistory(newState);
                      autoSaveDraft(newState, messages);
                      
                      // Track updated step
                      const updatedStep = newState.steps.find((s: TutorialStep) => s.id === stepId);
                      if (updatedStep) {
                        setUpdatedSteps(new Set([stepId]));
                        setTimeout(() => setUpdatedSteps(new Set()), 3000);
                      }
                      
                      toast.success(`Content generated for "${step.title}"`);
                    }
                  }
                } catch (parseError) {
                  console.error('Failed to parse SSE data:', parseError);
                }
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error generating content:', error);
          toast.error('Failed to generate content. Try again.');
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      // Show in chat
      const prompt = `Generate detailed content for this tutorial step:\n\nTitle: ${step.title}\n\nContext: This is part of a tutorial titled "${tutorialState.title}". ${tutorialState.description ? `Description: ${tutorialState.description}` : ''}\n\nPlease create comprehensive, well-structured content for this step. Use markdown formatting where appropriate.`;
      await sendMessage(prompt);
    }
  };

  const handleDeleteStep = (stepId: string) => {
    if (window.confirm('Are you sure you want to delete this step?')) {
      setTutorialState(prev => {
        const updatedSteps = prev.steps.filter(s => s.id !== stepId).map((step, index) => ({
          ...step,
          order: index + 1
        }));
        const newState = { ...prev, steps: updatedSteps };
        addToHistory(newState);
        autoSaveDraft(newState, messages);
        return newState;
      });
      toast.success('Step deleted');
    }
  };

  const toggleStepExpand = (stepId: string) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  // Preview template before applying
  const previewTemplate = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) {
      toast.error('Template not found');
      return;
    }

    if (!template.structure || !template.structure.sections) {
      toast.error('Invalid template structure');
      return;
    }

    setTemplatePreview(template);
    setSelectedSections(new Set(template.structure.sections.map((s: any) => s.id)));
    setShowTemplatePreview(true);
  };

  // Apply template with selected sections
  const applyTemplate = async (templateId: string, sectionIds?: string[]) => {
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) {
        toast.error('Template not found');
        return;
      }

      if (!template.structure || !template.structure.sections) {
        toast.error('Invalid template structure');
        return;
      }

      const existingSteps = tutorialState.steps.length;
      
      if (existingSteps === 0) {
        // Filter sections if specific ones were selected
        let sectionsToCreate = template.structure.sections;
        if (sectionIds && sectionIds.length > 0) {
          sectionsToCreate = template.structure.sections.filter((s: any) => sectionIds.includes(s.id));
        }

        // Create steps from selected sections - ensure all have unique IDs
        const templateSteps = sectionsToCreate
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
          .map((section: any, index: number) => ({
            id: `step-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
            title: section.title || `Step ${index + 1}`,
            content: '',
            type: section.type === 'step' ? 'text' : 
                  section.type === 'image' ? 'text' :
                  section.type === 'list' ? 'text' : 'text',
            order: section.order || (index + 1),
            metadata: section.properties || {}
          }));

        const newState: TutorialState = {
          ...tutorialState,
          title: tutorialState.title || template.name,
          description: tutorialState.description || template.description || '',
          steps: templateSteps
        };

        setTutorialState(newState);
        addToHistory(newState);
        
        // Friendly message with guidance
        const sectionsCount = templateSteps.length;
        const templateMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `Great! I've created ${sectionsCount} section${sectionsCount > 1 ? 's' : ''} based on the "${template.name}" template.\n\n💡 **Next Steps:**\n1. Click on any section title to edit it\n2. Use the ✨ magic wand button to have me generate content for that section\n3. Or type in the chat: "Generate content for [section name]"\n\nI recommend starting with the first section and working through them one at a time. Would you like me to generate content for the first section?`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, templateMessage]);

        // Auto-focus first step if it exists
        if (templateSteps.length > 0) {
          setFocusedStepId(templateSteps[0].id);
          setWorkflowMode('focused');
        }
      } else {
        // Tutorial has content - use AI to reorganize
        const templatePrompt = `I'd like to reorganize the current tutorial content using the "${template.name}" template structure. The template defines ${template.structure.sections.length} sections.\n\nPlease help me reorganize the existing ${existingSteps} step${existingSteps > 1 ? 's' : ''} according to this template's structure, preserving all the content but reordering and restructuring it appropriately.`;
        await sendMessage(templatePrompt);
      }

      setSelectedTemplate(templateId);
      setShowTemplates(false);
      setShowTemplatePreview(false);
      setTemplatePreview(null);
      toast.success(`Template "${template.name}" applied`);
    } catch (error) {
      console.error('Failed to apply template:', error);
      toast.error('Failed to apply template');
    }
  };

  // Get similar content
  const loadSimilarContent = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/ai-assistant/suggest-similar?category=${encodeURIComponent(tutorialState.category)}`,
        {
          headers: {
            'Authorization': `Bearer ${getValidToken() || ''}`
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        const similar = (data.tutorials || []).slice(0, 5);
        setSimilarTutorials(similar);
        setShowSimilarContent(true);
      }
    } catch (error) {
      console.error('Failed to load similar content:', error);
    }
  };

  // Share draft
  const handleShareDraft = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-assistant/share-draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getValidToken() || ''}`
        },
        body: JSON.stringify({
          tutorialState,
          messages
        })
      });
      
      if (!response.ok) {
        // Check if it's an authentication error
        if (response.status === 401 || response.status === 403) {
          await response.json().catch(() => ({ error: 'Authentication failed' }));
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token'); // Remove both for compatibility
          localStorage.removeItem('user');
          toast.error('Your session has expired. Please log in again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }
        
        const errorData = await response.json().catch(() => ({ error: 'Failed to share draft' }));
        throw new Error(errorData.error || 'Failed to share draft');
      }
      
      const data = await response.json();
      setShareToken(data.token);
      setShowShareModal(true);
      const shareUrl = `${window.location.origin}/builder/shared/${data.token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
    } catch (error: any) {
      console.error('Failed to share draft:', error);
      toast.error(error.message || 'Failed to create share link');
    }
  };

  // Recursively remove tutorialId from any object
  const removeTutorialId = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
      return obj.map(item => removeTutorialId(item));
    }
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (key === 'tutorialId' || key === 'pdfFile') {
          continue; // Skip these fields
        }
        cleaned[key] = removeTutorialId(obj[key]);
      }
      return cleaned;
    }
    return obj;
  };

  // Clean step for saving - remove backend-only fields
  const normalizeStepForSave = (step: TutorialStep, index: number) => {
    const stepAny = step as any;
    const cleanStep: any = {
      id: stepAny.id,
      title: stepAny.title,
      content: stepAny.content || '',
      type: stepAny.type,
      order: index + 1,
      videoUrl: stepAny.videoUrl || '',
      pdfUrl: stepAny.pdfUrl || ''
    };

    // Include metadata if it exists
    if (stepAny.metadata) {
      cleanStep.metadata = removeTutorialId(stepAny.metadata);
    }
    
    // Include subsections if they exist
    if (stepAny.subsections && Array.isArray(stepAny.subsections)) {
      cleanStep.subsections = stepAny.subsections.map((sub: any) => removeTutorialId(sub));
    }

    // Handle quiz questions
    if (stepAny.type === 'quiz' && Array.isArray(stepAny.questions)) {
      cleanStep.questions = stepAny.questions.map((q: any, qi: number) => {
        const cleanedQ = removeTutorialId(q);
        return {
          ...cleanedQ,
          title: (cleanedQ.title && cleanedQ.title.trim())
            ? cleanedQ.title
            : (cleanedQ.question?.trim()?.slice(0, 80) || `Question ${qi + 1}`)
        };
      });
    } else if (stepAny.questions) {
      cleanStep.questions = removeTutorialId(stepAny.questions);
    }

    // Final cleanup - recursively remove tutorialId from the entire step
    const finalClean = removeTutorialId(cleanStep);
    
    // Ensure these specific fields are deleted
    delete finalClean.tutorialId;
    delete finalClean.pdfFile;

    return finalClean;
  };

  const handleSave = async () => {
    if (!tutorialState.title.trim() || tutorialState.steps.length === 0) {
      toast.error('Please ensure the tutorial has a title and at least one step');
      return;
    }

    setIsSaving(true);
    try {
      // Clean steps before saving - remove tutorialId and other backend-only fields
      const cleanedSteps = tutorialState.steps.map((step, index) => {
        const normalized = normalizeStepForSave(step, index);
        // Final safety check: recursively remove tutorialId
        return removeTutorialId(normalized);
      });

      if (isEditing && id) {
        // For updates, exclude 'type' field as it's not allowed in update schema
        const { type, ...tutorialDataForUpdate } = tutorialState;
        const tutorialData = {
          ...tutorialDataForUpdate,
          steps: cleanedSteps,
          isPublished: true
        };
        
        await apiService.updateTutorial(id, tutorialData);
        toast.success('Tutorial updated successfully!');
        localStorage.removeItem('ai-builder-draft');
        // Navigate to the tutorial viewer
        navigate(`/tutorials/${id}`);
      } else {
        // For creates, include 'type' field as it's required in create schema
        const tutorialData = {
          ...tutorialState,
          steps: cleanedSteps,
          isPublished: true // Publish by default so it appears in the tutorials list
        };
        
        const createdTutorial = await apiService.createTutorial(tutorialData);
        toast.success('Tutorial saved and published!');
        localStorage.removeItem('ai-builder-draft');
        // Navigate to the newly created tutorial
        if (createdTutorial && createdTutorial.id) {
          navigate(`/tutorials/${createdTutorial.id}`);
        } else {
          // Fallback if ID is not in response
          navigate('/admin/tutorials');
        }
      }
    } catch (error: any) {
      console.error('Error saving tutorial:', error);
      toast.error(error?.response?.data?.error || 'Failed to save tutorial');
    } finally {
      setIsSaving(false);
    }
  };

  const getStepIcon = (type: string) => {
    const icons: Record<string, React.ElementType> = {
      text: FileText,
      video: Video,
      code: Code,
      quiz: HelpCircle,
      pdf: FileText,
      'work-instruction-step': BookOpen,
      'safety-warning': AlertCircle,
      'material-list': FileText
    };
    return icons[type] || FileText;
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header - Fixed at top */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {tutorialState.title || 'AI-Assisted Tutorial Builder'}
              </h1>
              {isEditing && <p className="text-sm text-gray-500">Editing existing tutorial</p>}
              {lastSaved && (
                <p className="text-xs text-gray-400 flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>Auto-saved {lastSaved.toLocaleTimeString()}</span>
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            {!isOnline && (
              <div className="flex items-center space-x-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Offline</span>
              </div>
            )}
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Templates"
            >
              <Sparkles className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={loadSimilarContent}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Similar Content"
            >
              <FileText className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={handleShareDraft}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Share Draft"
            >
              <Share2 className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={undo}
              disabled={history.past.length === 0}
              className={cn(
                "p-2 rounded-lg transition-colors",
                history.past.length > 0 
                  ? "hover:bg-gray-100 text-gray-700" 
                  : "text-gray-300 cursor-not-allowed"
              )}
            >
              <Undo className="w-5 h-5" />
            </button>
            <button
              onClick={redo}
              disabled={history.future.length === 0}
              className={cn(
                "p-2 rounded-lg transition-colors",
                history.future.length > 0 
                  ? "hover:bg-gray-100 text-gray-700" 
                  : "text-gray-300 cursor-not-allowed"
              )}
            >
              <Redo className="w-5 h-5" />
            </button>
            {/* Layout Selector */}
            <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg">
              <span className="text-sm text-gray-600 mr-2">Layout:</span>
              <button
                onClick={() => setTutorialState(prev => ({ ...prev, layout: 'steps' }))}
                className={cn(
                  "px-3 py-1 text-sm rounded transition-colors",
                  tutorialState.layout === 'steps'
                    ? "bg-[#667eea] text-white"
                    : "bg-white text-gray-700 hover:bg-gray-200"
                )}
                title="Step-by-step navigation"
              >
                Steps
              </button>
              <button
                onClick={() => setTutorialState(prev => ({ ...prev, layout: 'continuous' }))}
                className={cn(
                  "px-3 py-1 text-sm rounded transition-colors",
                  tutorialState.layout === 'continuous'
                    ? "bg-[#667eea] text-white"
                    : "bg-white text-gray-700 hover:bg-gray-200"
                )}
                title="Continuous scroll"
              >
                Scroll
              </button>
            </div>
            <button
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className={cn(
                "flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors",
                isPreviewMode 
                  ? "bg-blue-100 text-blue-700" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              <Eye className="w-4 h-4" />
              <span>Preview</span>
            </button>
            <button
              onClick={() => {
                console.log('Extract Technical Terms clicked', { 
                  title: tutorialState.title, 
                  stepsCount: tutorialState.steps.length,
                  showGlossaryExtraction 
                });
                setShowGlossaryExtraction(true);
              }}
              disabled={!tutorialState.title || tutorialState.steps.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-4 h-4" />
              <span>Extract Technical Terms</span>
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Saving...' : 'Save'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Preview Mode */}
      {isPreviewMode ? (
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Tutorial Header */}
            <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {tutorialState.title || 'Untitled Tutorial'}
              </h1>
              {tutorialState.description && (
                <p className="text-lg text-gray-600 mb-6">
                  {tutorialState.description}
                </p>
              )}
              
              {/* Metadata */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{tutorialState.estimatedTime || 'Not specified'}</span>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full capitalize">
                  {tutorialState.type}
                </span>
                {tutorialState.category && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full">
                    {tutorialState.category}
                  </span>
                )}
              </div>
            </div>

            {/* Steps */}
            {tutorialState.steps.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No steps yet. Start chatting with the AI to create your tutorial!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {tutorialState.steps.map((step, index) => (
                  <motion.div
                    key={step.id}
                    id={`step-${step.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="bg-white rounded-lg shadow-sm overflow-hidden"
                  >
                    {/* Step Header */}
                    <div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] px-6 py-4 text-white">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 rounded-full font-bold">
                          {index + 1}
                        </div>
                        <h2 className="text-2xl font-semibold">{step.title}</h2>
                      </div>
                    </div>

                    {/* Step Content */}
                    <div className="p-6 prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6 prose-li:my-2 prose-strong:font-bold prose-strong:text-gray-900 prose-a:text-blue-600 prose-a:underline prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic">
                      {/* Metadata Display - Materials, Tools, Warnings */}
                      {step.metadata && (
                        <div className="mb-6 space-y-4">
                          {/* Materials & Tools */}
                          {((step.metadata.materials?.length ?? 0) > 0 || (step.metadata.tools?.length ?? 0) > 0) && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h4 className="font-semibold text-blue-900 mb-3">Materials & Tools</h4>
                              <div className="grid grid-cols-2 gap-4">
                                {step.metadata.materials && step.metadata.materials.length > 0 && (
                                  <div>
                                    <h5 className="text-sm font-medium text-blue-800 mb-2">Materials:</h5>
                                    <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                                      {step.metadata.materials.map((material, idx) => (
                                        <li key={idx}>{material}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {step.metadata.tools && step.metadata.tools.length > 0 && (
                                  <div>
                                    <h5 className="text-sm font-medium text-blue-800 mb-2">Tools:</h5>
                                    <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                                      {step.metadata.tools.map((tool, idx) => (
                                        <li key={idx}>{tool}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Safety Warnings & Cautions */}
                          {((step.metadata.warnings?.length ?? 0) > 0 || (step.metadata.cautions?.length ?? 0) > 0) && (
                            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                              <div className="flex items-center space-x-2 mb-3">
                                <AlertCircle className="w-5 h-5 text-red-600" />
                                <h4 className="font-bold text-red-900">Safety Warnings</h4>
                              </div>
                              <ul className="space-y-2">
                                {step.metadata.warnings?.map((warning, idx) => (
                                  <li key={`warning-${step.id}-${idx}`} className="flex items-start space-x-2">
                                    <span className="text-red-600 font-bold">⚠</span>
                                    <span className="text-sm text-red-800">{warning}</span>
                                  </li>
                                ))}
                                {step.metadata.cautions?.map((caution, idx) => (
                                  <li key={`caution-${step.id}-${idx}`} className="flex items-start space-x-2">
                                    <span className="text-yellow-600 font-bold">!</span>
                                    <span className="text-sm text-red-700">{caution}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Prerequisites */}
                          {step.metadata.prerequisites && step.metadata.prerequisites.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                              <h4 className="font-semibold text-yellow-900 mb-2">Prerequisites</h4>
                              <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                                {step.metadata.prerequisites.map((prereq, idx) => (
                                  <li key={idx}>{prereq}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Location & Quantity Info */}
                          {(step.metadata.location || step.metadata.quantity || step.metadata.duration) && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="grid grid-cols-3 gap-4 text-sm">
                                {step.metadata.location && (
                                  <div>
                                    <span className="font-medium text-gray-700">Location:</span>
                                    <p className="text-gray-600">{step.metadata.location}</p>
                                  </div>
                                )}
                                {step.metadata.quantity && (
                                  <div>
                                    <span className="font-medium text-gray-700">Quantity:</span>
                                    <p className="text-gray-600">{step.metadata.quantity}</p>
                                  </div>
                                )}
                                {step.metadata.duration && (
                                  <div>
                                    <span className="font-medium text-gray-700">Duration:</span>
                                    <p className="text-gray-600">{step.metadata.duration}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* References */}
                          {step.metadata.references && step.metadata.references.length > 0 && (
                            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                              <h4 className="font-semibold text-indigo-900 mb-2">References</h4>
                              <ul className="space-y-2">
                                {step.metadata.references.map((ref) => (
                                  <li key={ref.id} className="flex items-center space-x-2">
                                    <FileText className="w-4 h-4 text-indigo-600" />
                                    {ref.url ? (
                                      <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-700 hover:text-indigo-900 underline">
                                        {ref.title}
                                      </a>
                                    ) : (
                                      <span className="text-sm text-indigo-700">{ref.title}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Subsections */}
                      {step.subsections && step.subsections.length > 0 && (
                        <div className="mb-6 space-y-3">
                          {step.subsections
                            .sort((a, b) => a.order - b.order)
                            .map((subsection) => (
                              <div key={subsection.id} className={cn(
                                "border rounded-lg p-3",
                                subsection.type === 'warning' 
                                  ? "bg-red-50 border-red-200"
                                  : subsection.type === 'note'
                                    ? "bg-blue-50 border-blue-200"
                                    : "bg-gray-50 border-gray-200"
                              )}>
                                {subsection.title && (
                                  <h5 className={cn(
                                    "font-semibold mb-2",
                                    subsection.type === 'warning' ? "text-red-900" : "text-gray-900"
                                  )}>
                                    {subsection.title}
                                  </h5>
                                )}
                                {subsection.type === 'list' && subsection.items ? (
                                  <ul className="list-disc list-inside space-y-1 text-sm">
                                    {subsection.items.map((item, idx) => (
                                      <li key={idx}>{item}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div 
                                    className={cn(
                                      "text-sm prose prose-sm max-w-none",
                                      subsection.type === 'warning' ? "text-red-800" : "text-gray-700"
                                    )}
                                    dangerouslySetInnerHTML={{ 
                                      __html: subsection.content 
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                        </div>
                      )}

                      {step.type === 'text' && step.content && (
                        <div 
                          className="tutorial-content prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6 prose-li:my-2 prose-strong:font-bold prose-strong:text-gray-900 prose-a:text-blue-600 prose-a:underline prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic"
                          dangerouslySetInnerHTML={{ 
                            __html: renderMarkdown(step.content) 
                          }}
                        />
                      )}
                      
                      {step.type === 'work-instruction-step' && step.content && (
                        <div className="bg-white border border-gray-300 rounded-lg p-4">
                          <div 
                            className="tutorial-content prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6 prose-li:my-2 prose-strong:font-bold prose-strong:text-gray-900 prose-a:text-blue-600 prose-a:underline prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic"
                            dangerouslySetInnerHTML={{ 
                              __html: renderMarkdown(step.content) 
                            }}
                          />
                        </div>
                      )}
                      
                      {step.type === 'safety-warning' && step.content && (
                        <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                            <div 
                              className="text-red-900 font-medium prose prose-lg max-w-none"
                              dangerouslySetInnerHTML={{ 
                                __html: renderMarkdown(step.content) 
                              }}
                            />
                          </div>
                        </div>
                      )}
                      
                      {step.type === 'material-list' && step.content && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">Materials Required</h4>
                          <div 
                            className="text-blue-800 prose prose-lg max-w-none"
                            dangerouslySetInnerHTML={{ 
                              __html: renderMarkdown(step.content) 
                            }}
                          />
                        </div>
                      )}

                      {step.type === 'code' && step.content && (
                        <div className="my-4">
                          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                            <code>{step.content}</code>
                          </pre>
                        </div>
                      )}

                      {step.type === 'video' && step.videoUrl && (
                        <div className="my-4">
                          <video 
                            src={step.videoUrl} 
                            controls
                            className="w-full rounded-lg shadow-md"
                          />
                          {step.content && (
                            <div 
                              className="text-gray-600 mt-2 prose prose-lg max-w-none"
                              dangerouslySetInnerHTML={{ 
                                __html: renderMarkdown(step.content) 
                              }}
                            />
                          )}
                        </div>
                      )}

                      {step.type === 'pdf' && step.pdfUrl && (
                        <div className="my-4">
                          <iframe 
                            src={step.pdfUrl} 
                            className="w-full h-96 rounded-lg shadow-md border"
                            title={step.title}
                          />
                          {step.content && (
                            <div 
                              className="text-gray-600 mt-2 prose prose-lg max-w-none"
                              dangerouslySetInnerHTML={{ 
                                __html: renderMarkdown(step.content) 
                              }}
                            />
                          )}
                        </div>
                      )}

                      {step.type === 'quiz' && step.questions && step.questions.length > 0 && (
                        <div className="my-4 space-y-4">
                          {step.questions.map((question: any, qIdx: number) => (
                            <div key={question.id || qIdx} className="border border-gray-200 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-2">{question.question || question.title}</h4>
                              <ul className="space-y-2">
                                {question.options.map((option: string, oIdx: number) => (
                                  <li key={oIdx} className="flex items-start space-x-2">
                                    <input 
                                      type="radio" 
                                      name={`question-${question.id || qIdx}`} 
                                      id={`q${question.id || qIdx}-o${oIdx}`}
                                      className="mt-1"
                                      disabled
                                    />
                                    <label htmlFor={`q${question.id || qIdx}-o${oIdx}`} className="text-gray-700">
                                      {option}
                                    </label>
                                  </li>
                                ))}
                              </ul>
                              {question.explanation && (
                                <p className="text-sm text-gray-600 mt-2 italic">{question.explanation}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Fallback for content without specific type rendering */}
                      {step.content && 
                       !['text', 'code', 'work-instruction-step', 'safety-warning', 'material-list'].includes(step.type) && 
                       (!step.videoUrl || step.type !== 'video') && 
                       (!step.pdfUrl || step.type !== 'pdf') && 
                       (!step.questions || step.type !== 'quiz') && (
                        <div 
                          className="text-gray-700 prose prose-lg max-w-none"
                          dangerouslySetInnerHTML={{ 
                            __html: renderMarkdown(step.content) 
                          }}
                        />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Chat Interface */}
          <div className="flex-1 flex flex-col relative min-h-0">
          {/* Messages - Scrollable area between header and input */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-6 space-y-4"
            style={{ 
              paddingBottom: '140px' // Space for fixed input area at bottom
            }}
          >
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "flex",
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={cn(
                    "max-w-3xl rounded-2xl px-6 py-4",
                    message.role === 'user'
                      ? "bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white"
                      : "bg-white border border-gray-200 text-gray-900"
                  )}
                >
                  <div className="prose prose-sm max-w-none">
                    {message.content ? (
                      message.content.split('\n').map((line, idx) => (
                        <p key={idx} className="mb-2 last:mb-0">
                          {line}
                        </p>
                      ))
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Loader className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Thinking...</span>
                      </div>
                    )}
                  </div>
                  {message.mediaUrls && message.mediaUrls.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {message.mediaUrls.map((url, idx) => {
                        const fileName = url.split('/').pop() || `File ${idx + 1}`;
                        const isDocument = fileName.includes('.pdf') || 
                                         fileName.includes('.docx') || 
                                         fileName.includes('.doc') || 
                                         fileName.includes('.pptx') || 
                                         fileName.includes('.ppt');
                        
                        return isDocument ? (
                          <div key={idx} className={cn(
                            "flex items-center space-x-2 p-3 rounded-lg border",
                            message.role === 'user'
                              ? "bg-white/10 border-white/20"
                              : "bg-gray-50 border-gray-200"
                          )}>
                            <FileText className={cn(
                              "w-5 h-5",
                              message.role === 'user' ? "text-white" : "text-gray-600"
                            )} />
                            <span className={cn(
                              "text-sm font-medium",
                              message.role === 'user' ? "text-white" : "text-gray-700"
                            )}>
                              {fileName}
                            </span>
                          </div>
                        ) : (
                          <img
                            key={idx}
                            src={url}
                            alt={`Uploaded media ${idx + 1}`}
                            className="rounded-lg max-w-full h-auto"
                          />
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Step Preview Cards - Show when AI creates steps */}
                  {message.role === 'assistant' && ((message.createdSteps?.length ?? 0) > 0 || (message.updatedSteps?.length ?? 0) > 0) && (
                    <div className="mt-4 space-y-2">
                      {(() => {
                        // Get unique step IDs and filter out null/undefined
                        const uniqueStepIds = [...new Set(message.createdSteps?.filter(id => id) || [])];
                        console.log('Rendering step previews for IDs:', uniqueStepIds);
                        console.log('Available steps:', tutorialState.steps.map(s => ({ id: s.id, title: s.title })));
                        
                        return uniqueStepIds.map((stepId, idx) => {
                          const step = tutorialState.steps.find(s => s.id === stepId);
                          if (!step) {
                            console.warn(`Step ${stepId} not found in tutorialState`);
                            return null;
                          }
                          const Icon = getStepIcon(step.type);
                          return (
                            <motion.div
                              key={`created-${stepId}-${idx}`}
                              initial={{ opacity: 0, scale: 0.95, y: 10 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4"
                            >
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-[#667eea] to-[#764ba2] rounded-lg flex items-center justify-center">
                                  <Icon className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <SparklesIcon className="w-3 h-3 text-blue-600" />
                                    <span className="text-xs font-semibold text-blue-700 uppercase">New Step</span>
                                  </div>
                                  <h4 className="font-semibold text-gray-900 mb-1">{step.title}</h4>
                                  {step.content && (
                                    <p className="text-sm text-gray-600 line-clamp-2">
                                      {step.content.replace(/<[^>]*>/g, '').substring(0, 120)}...
                                    </p>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          );
                        });
                      })()}
                      {message.updatedSteps?.filter(id => id).map((stepId, idx) => {
                        const step = tutorialState.steps.find(s => s.id === stepId);
                        if (!step) return null;
                        return (
                          <motion.div
                            key={`updated-${stepId}-${idx}`}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"
                          >
                            <div className="flex items-center space-x-2">
                              <CheckCircle2 className="w-4 h-4 text-yellow-600" />
                              <span className="text-xs font-medium text-yellow-700">Updated: {step.title}</span>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area - Fixed at bottom of chat interface */}
          <div 
            className={cn(
              "absolute bottom-0 left-0 right-0 z-40 border-t bg-white p-4 shadow-lg transition-colors",
              isDragOver 
                ? "border-blue-400 bg-blue-50 border-2" 
                : "border-gray-200"
            )}
            style={{ width: '100%' }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDragOver && (
              <div className="absolute inset-0 bg-blue-100 bg-opacity-50 border-2 border-dashed border-blue-400 rounded-lg flex items-center justify-center z-50 pointer-events-none">
                <div className="text-center">
                  <Paperclip className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                  <p className="text-blue-700 font-semibold">Drop files here to attach</p>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex items-end space-x-3">
              <div className="flex-1 flex items-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowMediaUpload(true)}
                  className={cn(
                    "p-3 rounded-lg transition-all flex items-center justify-center relative border-2",
                    pendingMediaFiles.length > 0
                      ? "bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent hover:border-gray-300"
                  )}
                  title="Upload files (PDF, Word, PowerPoint, Images)"
                >
                  <Paperclip className="w-5 h-5" />
                  {pendingMediaFiles.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {pendingMediaFiles.length}
                    </span>
                  )}
                </button>
                <div className="flex-1 flex flex-col">
                  {pendingMediaFiles.length > 0 && (
                    <div className="flex items-center flex-wrap gap-2 mb-2 px-1">
                      {pendingMediaFiles.map((file, idx) => {
                        const isVideo = file.type.startsWith('video/');
                        const isImage = file.type.startsWith('image/');
                        const isDoc = file.type === 'application/pdf' || 
                                     file.name.endsWith('.docx') || 
                                     file.name.endsWith('.doc') || 
                                     file.name.endsWith('.pptx') || 
                                     file.name.endsWith('.ppt');
                        return (
                          <div
                            key={idx}
                            className={cn(
                              "flex items-center space-x-1 px-2 py-1 rounded-full text-xs",
                              isVideo && "bg-purple-100 text-purple-700",
                              isImage && "bg-blue-100 text-blue-700",
                              isDoc && "bg-green-100 text-green-700",
                              !isVideo && !isImage && !isDoc && "bg-gray-100 text-gray-700"
                            )}
                            title={file.name}
                          >
                            {isVideo ? (
                              <Video className="w-3 h-3" />
                            ) : isImage ? (
                              <ImageIcon className="w-3 h-3" />
                            ) : (
                              <FileText className="w-3 h-3" />
                            )}
                            <span className="max-w-[80px] truncate">{file.name}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const newFiles = pendingMediaFiles.filter((_, i) => i !== idx);
                                const newUrls = pendingMediaUrls.filter((_, i) => i !== idx);
                                setPendingMediaFiles(newFiles);
                                setPendingMediaUrls(newUrls);
                              }}
                              className="ml-1 hover:bg-black hover:bg-opacity-10 rounded-full p-0.5"
                              title="Remove attachment"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={pendingMediaFiles.length > 0 
                      ? (() => {
                          const videoCount = pendingMediaFiles.filter(f => f.type.startsWith('video/')).length;
                          const docCount = pendingMediaFiles.filter(f => 
                            f.type === 'application/pdf' || 
                            f.name.endsWith('.docx') || 
                            f.name.endsWith('.doc') || 
                            f.name.endsWith('.pptx') || 
                            f.name.endsWith('.ppt')
                          ).length;
                          if (videoCount > 0) {
                            return `Tell me what you'd like to do with the uploaded video${videoCount > 1 ? 's' : ''}...`;
                          } else if (docCount > 0) {
                            return `Describe what you'd like to do with the uploaded ${docCount} document${docCount > 1 ? 's' : ''}...`;
                          } else {
                            return `Describe what you'd like to do with the uploaded ${pendingMediaFiles.length} file${pendingMediaFiles.length > 1 ? 's' : ''}...`;
                          }
                        })()
                      : "Describe your tutorial idea, upload documents, or ask for help..."
                    }
                    rows={2}
                    className={cn(
                      "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent resize-none",
                      pendingMediaFiles.length > 0 
                        ? "border-blue-300 bg-blue-50" 
                        : "border-gray-300",
                      isDragOver ? "border-blue-400 bg-blue-50" : ""
                    )}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    onPaste={(e) => {
                      // Allow default paste behavior for text
                      // The textarea will automatically handle text pasting
                      // If files are pasted, handle them
                      const items = e.clipboardData.items;
                      if (items) {
                        const files: File[] = [];
                        for (let i = 0; i < items.length; i++) {
                          const item = items[i];
                          if (item.kind === 'file') {
                            const file = item.getAsFile();
                            if (file) {
                              files.push(file);
                            }
                          }
                        }
                        if (files.length > 0) {
                          e.preventDefault();
                          // Handle pasted files
                          handlePastedFiles(files);
                        }
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragOver(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Only set isDragOver to false if we're leaving the textarea itself
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX;
                      const y = e.clientY;
                      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                        setIsDragOver(false);
                      }
                    }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragOver(false);

                      const files = Array.from(e.dataTransfer.files);
                      if (files.length > 0) {
                        await handlePastedFiles(files);
                      }
                    }}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                className={cn(
                  "p-3 rounded-lg transition-colors",
                  isRecording 
                    ? "bg-red-500 text-white hover:bg-red-600" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                )}
                title={isRecording ? 'Stop Recording - Click Send when finished' : 'Start Voice Input'}
              >
                {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              <button
                type="submit"
                disabled={isLoading || (!input.trim() && pendingMediaUrls.length === 0)}
                className="p-3 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title={pendingMediaFiles.length > 0 && !input.trim() ? "Add a message about what to do with the uploaded files" : "Send message"}
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar - Tutorial Structure */}
        <div className="w-96 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Tutorial Structure</h3>
            
            {/* Metadata */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={tutorialState.title}
                  onChange={(e) => handleManualEdit('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Tutorial title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={tutorialState.description}
                  onChange={(e) => handleManualEdit('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={tutorialState.type}
                  onChange={(e) => handleManualEdit('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="guide">Guide</option>
                  <option value="course">Course</option>
                  <option value="presentation">Presentation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <div className="flex space-x-2">
                  <select
                    value={tutorialState.category}
                    onChange={(e) => handleManualEdit('category', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm"
                    disabled={loadingCategories}
                  >
                    {loadingCategories ? (
                      <option>Loading categories...</option>
                    ) : (
                      (() => {
                        // Build hierarchy display for categories
                        const buildCategoryOptions = (cats: any[], parentId: string | null = null, level: number = 0): Array<{name: string, value: string, display: string}> => {
                          return cats
                            .filter(cat => {
                              const catParentId = cat.parentCategoryId || null;
                              return catParentId === parentId;
                            })
                            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                            .flatMap(cat => {
                              const displayName = level > 0 ? `${'  '.repeat(level)}└─ ${cat.name}` : cat.name;
                              const result = [{ name: cat.name, value: cat.name, display: displayName }];
                              const children = buildCategoryOptions(cats, cat.id, level + 1);
                              return [...result, ...children];
                            });
                        };
                        
                        const options = categoryObjects.length > 0 
                          ? buildCategoryOptions(categoryObjects)
                          : categories.map(name => ({ name, value: name, display: name }));
                        
                        return options.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.display}
                          </option>
                        ));
                      })()
                    )}
                  </select>
                  <button
                    onClick={() => setShowCategoryManager(true)}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Manage Categories"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <h4 className="font-medium text-gray-900">
                    Steps ({tutorialState.steps.length})
                  </h4>
                  {tutorialState.steps.length > 0 && (
                    <span className="text-xs text-gray-500">
                      {tutorialState.steps.filter(s => s.content && s.content.trim().length > 0).length} / {tutorialState.steps.length} with content
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {tutorialState.steps.length > 0 && (
                    <>
                      <button
                        onClick={() => {
                          if (workflowMode === 'focused') {
                            setWorkflowMode('overview');
                            setFocusedStepId(null);
                          } else {
                            // Find first step without content, or first step if all have content
                            const emptyStep = tutorialState.steps.find(s => !s.content || s.content.trim().length === 0);
                            const stepToFocus = emptyStep || tutorialState.steps[0];
                            if (stepToFocus) {
                              setFocusedStepId(stepToFocus.id);
                              setExpandedSteps(new Set([stepToFocus.id]));
                              setWorkflowMode('focused');
                            }
                          }
                        }}
                        className={cn(
                          "text-xs px-2 py-1 rounded transition-colors",
                          workflowMode === 'focused'
                            ? "bg-purple-100 text-purple-700"
                            : "text-gray-600 hover:bg-gray-100"
                        )}
                        title={workflowMode === 'focused' ? 'Exit focus mode - show all steps' : 'Focus mode: Work on one step at a time (auto-selects first empty step)'}
                      >
                        {workflowMode === 'focused' ? '📌 Focused' : '👁️ Focus'}
                      </button>
                      <button
                        onClick={() => {
                          if (expandedSteps.size === tutorialState.steps.length) {
                            setExpandedSteps(new Set());
                          } else {
                            setExpandedSteps(new Set(tutorialState.steps.map(s => s.id)));
                          }
                        }}
                        className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                        title="Expand/Collapse all"
                      >
                        {expandedSteps.size === tutorialState.steps.length ? 'Collapse All' : 'Expand All'}
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                {tutorialState.steps.map((step, index) => {
                  // Capture step ID immediately to avoid closure issues
                  // Generate ID if missing (shouldn't happen after our fix, but handle gracefully)
                  const stepId = step?.id || `step-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                  if (!step?.id) {
                    console.warn('Step missing ID at index:', index, 'Using generated ID:', stepId);
                  }
                  
                  const Icon = getStepIcon(step.type);
                  const isNew = newlyAddedSteps.has(stepId);
                  const isUpdated = updatedSteps.has(stepId);
                  const isExpanded = expandedSteps.has(stepId);
                  const isFocused = focusedStepId === stepId;
                  const hasContent = step.content && step.content.trim().length > 0;
                  const contentPreview = step.content?.replace(/<[^>]*>/g, '').substring(0, 100) || '';
                  const hasImage = step.type === 'text' && step.content?.includes('<img');
                  const hasVideo = step.videoUrl;
                  const hasCode = step.type === 'code';
                  
                  // In focused mode, hide non-focused steps (only if focusedStepId is set)
                  if (workflowMode === 'focused' && focusedStepId && focusedStepId !== stepId) {
                    return <React.Fragment key={stepId} />;
                  }

                  return (
                    <motion.div
                      key={stepId}
                      initial={isNew ? { opacity: 0, x: -20, scale: 0.95 } : false}
                      animate={isNew ? { opacity: 1, x: 0, scale: 1 } : {}}
                      transition={isNew ? { duration: 0.3, delay: index * 0.05 } : {}}
                      onClick={(e) => {
                        // Only handle clicks if not clicking on interactive elements (buttons, inputs, etc.)
                        const target = e.target as HTMLElement;
                        if (target.closest('button') || target.closest('input') || target.closest('textarea')) {
                          return; // Don't handle clicks on interactive elements
                        }
                        // Allow clicking steps to focus on them in focus mode
                        if (workflowMode === 'focused') {
                          setFocusedStepId(stepId);
                          setExpandedSteps(new Set([stepId]));
                        }
                      }}
                      className={cn(
                        "border rounded-lg transition-all",
                        isFocused || (workflowMode === 'focused' && focusedStepId === stepId)
                          ? "border-purple-400 bg-purple-50 shadow-lg ring-2 ring-purple-200"
                          : workflowMode === 'focused'
                            ? "opacity-0 h-0 overflow-hidden pointer-events-none"
                            : isNew 
                              ? "border-blue-400 bg-gradient-to-r from-blue-50 to-purple-50 shadow-md" 
                              : isUpdated
                                ? "border-yellow-300 bg-yellow-50"
                                : hasContent
                                  ? "border-green-200 bg-green-50"
                                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      )}
                    >
                      {/* Step Header */}
                      <div className="p-3">
                        <div className="flex items-start space-x-2">
                          <button
                            onClick={() => toggleStepExpand(stepId)}
                            className="flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                          <div className={cn(
                            "flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center",
                            isNew 
                              ? "bg-gradient-to-r from-[#667eea] to-[#764ba2]"
                              : hasContent
                                ? "bg-green-100"
                                : "bg-gray-100"
                          )}>
                            <Icon className={cn(
                              "w-4 h-4",
                              isNew ? "text-white" : hasContent ? "text-green-700" : "text-gray-600"
                            )} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <span className={cn(
                                  "text-xs font-medium flex-shrink-0",
                                  hasContent ? "text-green-600" : "text-gray-500"
                                )}>
                                  #{index + 1}
                                  {hasContent && <span className="ml-1 text-green-600" title="Has content">●</span>}
                                </span>
                                {editingStepId === stepId && editingStepField === 'title' ? (
                                <input
                                  type="text"
                                  value={step.title}
                                  onChange={(e) => handleStepEdit(stepId, 'title', e.target.value)}
                                  onBlur={() => {
                                    setEditingStepId(null);
                                    setEditingStepField(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      setEditingStepId(null);
                                      setEditingStepField(null);
                                    }
                                  }}
                                  autoFocus
                                  className="flex-1 text-sm font-semibold text-gray-900 px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              ) : (
                                <h5 
                                  className="text-sm font-semibold text-gray-900 truncate cursor-text hover:text-blue-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingStepId(stepId);
                                    setEditingStepField('title');
                                  }}
                                  title="Click to edit title"
                                >
                                  {step.title || `Step ${index + 1}`}
                                </h5>
                              )}
                              </div>
                              <div className="flex items-center space-x-2">
                                {isNew && (
                                  <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full"
                                  >
                                    New
                                  </motion.span>
                                )}
                                {isUpdated && !isNew && (
                                  <span className="text-xs font-medium text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">
                                    Updated
                                  </span>
                                )}
                                {/* Action Buttons */}
                                {!hasContent && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleGenerateStepContent(stepId, true); // Silent mode - no chat clutter
                                    }}
                                    className="p-1.5 text-purple-600 hover:bg-purple-100 rounded transition-colors"
                                    title="Generate content with AI (quiet mode)"
                                  >
                                    <Wand2 className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    console.log('Edit button clicked for stepId:', stepId);
                                    console.log('Step object:', step);
                                    console.log('Current editingStepModal state:', editingStepModal);
                                    
                                    const stepToEdit = tutorialState.steps.find(s => s.id === stepId);
                                    console.log('Step found in tutorialState:', stepToEdit ? 'YES' : 'NO');
                                    
                                    if (stepToEdit) {
                                      const contentToEdit = stepToEdit.content || '';
                                      console.log('Setting content (length):', contentToEdit.length);
                                      setEditingStepContent(contentToEdit);
                                      console.log('Setting modal state to step ID:', stepId);
                                      setEditingStepModal(stepId);
                                      console.log('State updated - editingStepModal should now be:', stepId);
                                    } else {
                                      console.error('Step not found! Looking for stepId:', stepId);
                                      console.error('Available step IDs:', tutorialState.steps.map(s => s.id));
                                    }
                                  }}
                                  className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                  title="Edit step in rich text editor"
                                  type="button"
                                  style={{ pointerEvents: 'auto', zIndex: 10 }}
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                {hasAvailableFrames && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenFrameSelector(stepId);
                                    }}
                                    className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                                    title="Select screenshots for this step"
                                    type="button"
                                  >
                                    <ImageIcon className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteStep(stepId);
                                  }}
                                  className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                                  title="Delete step"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Rich Preview - Only show if expanded */}
                            {isExpanded && (
                              <div className="mt-2 space-y-2">
                                {editingStepId === stepId && editingStepField === 'content' ? (
                                  <textarea
                                    value={step.content || ''}
                                    onChange={(e) => handleStepEdit(stepId, 'content', e.target.value)}
                                    onBlur={() => {
                                      setEditingStepId(null);
                                      setEditingStepField(null);
                                    }}
                                    autoFocus
                                    rows={3}
                                    className="w-full text-xs text-gray-700 px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                    placeholder="Enter step content..."
                                  />
                                ) : (
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingStepId(stepId);
                                      setEditingStepField('content');
                                    }}
                                    className="cursor-text hover:bg-gray-50 rounded px-2 py-1 -mx-2 -my-1"
                                    title="Click to edit content"
                                  >
                                    {contentPreview ? (
                                      <p className="text-xs text-gray-600 line-clamp-2">
                                        {contentPreview}
                                      </p>
                                    ) : (
                                      <p className="text-xs text-gray-400 italic">
                                        Click to add content...
                                      </p>
                                    )}
                                  </div>
                                )}
                                
                                {/* Metadata Preview */}
                                {step.metadata && (
                                  <div className="space-y-1.5">
                                    {step.metadata.materials && step.metadata.materials.length > 0 && (
                                      <div className="flex items-start space-x-1">
                                        <span className="text-xs font-medium text-blue-600">Materials:</span>
                                        <span className="text-xs text-gray-600">{step.metadata.materials.slice(0, 2).join(', ')}{step.metadata.materials.length > 2 ? '...' : ''}</span>
                                      </div>
                                    )}
                                    {step.metadata.warnings && step.metadata.warnings.length > 0 && (
                                      <div className="flex items-center space-x-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                                        <AlertCircle className="w-3 h-3" />
                                        <span className="font-medium">{step.metadata.warnings.length} warning{step.metadata.warnings.length > 1 ? 's' : ''}</span>
                                      </div>
                                    )}
                                    {step.metadata.location && (
                                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                                        <span className="font-medium">Location:</span>
                                        <span className="truncate">{step.metadata.location}</span>
                                      </div>
                                    )}
                                    {step.metadata.quantity && (
                                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                                        <span className="font-medium">Quantity:</span>
                                        <span>{step.metadata.quantity}</span>
                                      </div>
                                    )}
                                    {step.metadata.references && step.metadata.references.length > 0 && (
                                      <div className="flex items-center space-x-1 text-xs text-blue-600">
                                        <FileText className="w-3 h-3" />
                                        <span>{step.metadata.references.length} reference{step.metadata.references.length > 1 ? 's' : ''}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Media Indicators */}
                                <div className="flex items-center space-x-2 flex-wrap">
                                  {hasImage && (
                                    <span className="inline-flex items-center space-x-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                      <ImageIcon className="w-3 h-3" />
                                      <span>Image</span>
                                    </span>
                                  )}
                                  {hasVideo && (
                                    <span className="inline-flex items-center space-x-1 text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                                      <Video className="w-3 h-3" />
                                      <span>Video</span>
                                    </span>
                                  )}
                                  {hasCode && (
                                    <span className="inline-flex items-center space-x-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                                      <Code className="w-3 h-3" />
                                      <span>Code</span>
                                    </span>
                                  )}
                                  {step.type === 'quiz' && step.questions && (
                                    <span className="inline-flex items-center space-x-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                                      <HelpCircle className="w-3 h-3" />
                                      <span>{step.questions.length} questions</span>
                                    </span>
                                  )}
                                  {step.type === 'work-instruction-step' && (
                                    <span className="inline-flex items-center space-x-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                                      <BookOpen className="w-3 h-3" />
                                      <span>Work Instruction</span>
                                    </span>
                                  )}
                                  {step.type === 'safety-warning' && (
                                    <span className="inline-flex items-center space-x-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                                      <AlertCircle className="w-3 h-3" />
                                      <span>Safety Warning</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
                {tutorialState.steps.length === 0 && (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <BookOpen className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-3">
                      No steps yet. Start by:
                    </p>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p>• Select a template above</p>
                      <p>• Or chat with AI to create content</p>
                    </div>
                  </div>
                )}

                {/* Focused Mode Navigation */}
                {workflowMode === 'focused' && tutorialState.steps.length > 0 && focusedStepId && (
                  <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h5 className="font-semibold text-purple-900">🎯 Focus Mode</h5>
                        <p className="text-xs text-purple-700 mt-1">
                          Step {tutorialState.steps.findIndex(s => s.id === focusedStepId) + 1} of {tutorialState.steps.length} • Work on one step at a time
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setWorkflowMode('overview');
                          setFocusedStepId(null);
                        }}
                        className="text-xs px-2 py-1 text-purple-700 hover:bg-purple-100 rounded transition-colors"
                      >
                        Exit Focus
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => {
                          const currentIndex = tutorialState.steps.findIndex(s => s.id === focusedStepId);
                          if (currentIndex > 0) {
                            setFocusedStepId(tutorialState.steps[currentIndex - 1].id);
                            setExpandedSteps(new Set([tutorialState.steps[currentIndex - 1].id]));
                          }
                        }}
                        disabled={tutorialState.steps.findIndex(s => s.id === focusedStepId) === 0}
                        className="px-3 py-1.5 text-sm bg-white border border-purple-300 text-purple-700 rounded hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        ← Previous
                      </button>
                      <button
                        onClick={() => {
                          const currentIndex = tutorialState.steps.findIndex(s => s.id === focusedStepId);
                          if (currentIndex < tutorialState.steps.length - 1) {
                            setFocusedStepId(tutorialState.steps[currentIndex + 1].id);
                            setExpandedSteps(new Set([tutorialState.steps[currentIndex + 1].id]));
                          }
                        }}
                        disabled={tutorialState.steps.findIndex(s => s.id === focusedStepId) === tutorialState.steps.length - 1}
                        className="px-3 py-1.5 text-sm bg-white border border-purple-300 text-purple-700 rounded hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Template Preview Modal */}
      <AnimatePresence>
        {showTemplatePreview && templatePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowTemplatePreview(false)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full mx-auto mt-10 max-h-[85vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h3 className="text-xl font-bold">Template: {templatePreview.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{templatePreview.description}</p>
                </div>
                <button onClick={() => setShowTemplatePreview(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-4">
                  <p className="text-sm text-gray-700 mb-3">
                    Select which sections you'd like to include. You can always add more later.
                  </p>
                  <div className="flex items-center space-x-4 mb-4">
                    <button
                      onClick={() => setSelectedSections(new Set(templatePreview.structure.sections.map((s: any) => s.id)))}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setSelectedSections(new Set())}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Clear All
                    </button>
                    <span className="text-sm text-gray-500">
                      {selectedSections.size} of {templatePreview.structure.sections.length} selected
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {templatePreview.structure.sections
                    .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                    .map((section: any) => (
                      <label
                        key={section.id}
                        className={cn(
                          "flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors",
                          selectedSections.has(section.id)
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSections.has(section.id)}
                          onChange={(e) => {
                            const newSet = new Set(selectedSections);
                            if (e.target.checked) {
                              newSet.add(section.id);
                            } else {
                              newSet.delete(section.id);
                            }
                            setSelectedSections(newSet);
                          }}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{section.title}</span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                              {section.type}
                            </span>
                          </div>
                          {section.properties && Object.keys(section.properties).length > 0 && (
                            <p className="text-xs text-gray-600 mt-1">
                              {JSON.stringify(section.properties).substring(0, 100)}...
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
                <button
                  onClick={() => setShowTemplatePreview(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedSections.size === 0) {
                      toast.error('Please select at least one section');
                      return;
                    }
                    applyTemplate(templatePreview.id, Array.from(selectedSections));
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all"
                >
                  Create {selectedSections.size} Section{selectedSections.size > 1 ? 's' : ''}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Templates Modal */}
      <AnimatePresence>
        {showTemplates && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowTemplates(false)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto mt-20 max-h-[80vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-xl font-bold">Select Template</h3>
                <button onClick={() => setShowTemplates(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-2 gap-4">
                  {templates.map(template => (
                    <div
                      key={template.id}
                      className={cn(
                        "p-4 border rounded-lg transition-colors",
                        selectedTemplate === template.id && "border-purple-500 bg-purple-50"
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold">{template.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {template.structure?.sections?.length || 0} sections
                          </p>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => previewTemplate(template.id)}
                            className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                          >
                            Preview
                          </button>
                          <button
                            onClick={() => applyTemplate(template.id)}
                            className="px-3 py-1.5 text-sm bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all"
                          >
                            Use All
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Similar Content Modal */}
      <AnimatePresence>
        {showSimilarContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowSimilarContent(false)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto mt-20 max-h-[80vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-xl font-bold">Similar Tutorials</h3>
                <button onClick={() => setShowSimilarContent(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {similarTutorials.map(tutorial => (
                  <div key={tutorial.id} className="p-4 border rounded-lg mb-3">
                    <h4 className="font-semibold">{tutorial.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{tutorial.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && shareToken && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowShareModal(false)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-auto mt-20 p-6"
            >
              <h3 className="text-xl font-bold mb-4">Share Draft</h3>
              <p className="text-sm text-gray-600 mb-4">Share link copied to clipboard!</p>
              <div className="p-3 bg-gray-100 rounded-lg font-mono text-sm break-all">
                {window.location.origin}/builder/shared/{shareToken}
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="mt-4 w-full px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Draft Recovery Modal */}
      <AnimatePresence>
        {showDraftRecoveryModal && draftToRecover && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div 
              className="absolute inset-0 bg-black bg-opacity-50" 
              onClick={handleDiscardDraft}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-auto mt-20 p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Found a Draft</h3>
                  <p className="text-sm text-gray-500">Would you like to recover your previous work?</p>
                </div>
              </div>

              {draftToRecover.tutorialState.title && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Title:</p>
                  <p className="text-sm text-gray-600">{draftToRecover.tutorialState.title}</p>
                </div>
              )}

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleDiscardDraft}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Discard
                </button>
                <button
                  onClick={handleRecoverDraft}
                  className="px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all"
                >
                  Recover Draft
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Media Upload Modal */}
      <AnimatePresence>
        {showMediaUpload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div 
              className="absolute inset-0 bg-black bg-opacity-50" 
              onClick={() => setShowMediaUpload(false)} 
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white h-full w-full max-w-4xl mx-auto shadow-xl"
            >
              <div className="h-full overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Upload Media</h2>
                  <button
                    onClick={() => setShowMediaUpload(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <MediaUploader onUpload={handleMediaUpload} maxFiles={30} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Media Placement Modal */}
      <AnimatePresence>
        {showMediaPlacementModal && tutorialState.steps.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowMediaPlacementModal(false)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto mt-20 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Choose Where to Insert Media</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {pendingMediaFiles.length} file{pendingMediaFiles.length > 1 ? 's' : ''} uploaded. Select where to place {pendingMediaFiles.length === 1 ? 'it' : 'them'}.
                  </p>
                </div>
                <button
                  onClick={() => setShowMediaPlacementModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Insert Position
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setMediaPlacement({ stepId: null, position: 'new-step' })}
                      className={cn(
                        "p-3 border-2 rounded-lg text-left transition-colors",
                        mediaPlacement?.position === 'new-step' && !mediaPlacement?.stepId
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <div className="font-medium text-gray-900">New Step</div>
                      <div className="text-xs text-gray-600 mt-1">Create a new step with this media</div>
                    </button>
                    <button
                      onClick={() => setMediaPlacement({ stepId: tutorialState.steps[0]?.id || null, position: 'within' })}
                      className={cn(
                        "p-3 border-2 rounded-lg text-left transition-colors",
                        mediaPlacement?.position === 'within'
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <div className="font-medium text-gray-900">Within Step</div>
                      <div className="text-xs text-gray-600 mt-1">Add to existing step content</div>
                    </button>
                  </div>
                </div>

                {mediaPlacement?.position !== 'new-step' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Step
                    </label>
                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                      {tutorialState.steps.map((step, index) => (
                        <button
                          key={step.id}
                          onClick={() => setMediaPlacement(prev => prev ? { ...prev, stepId: step.id } : { stepId: step.id, position: 'within' })}
                          className={cn(
                            "w-full p-3 text-left border-b last:border-b-0 transition-colors hover:bg-gray-50",
                            mediaPlacement?.stepId === step.id
                              ? "bg-blue-50 border-blue-200"
                              : "border-gray-100"
                          )}
                        >
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-gray-500">#{index + 1}</span>
                            <span className="font-medium text-gray-900">{step.title || `Step ${index + 1}`}</span>
                          </div>
                          {step.content && (
                            <div className="text-xs text-gray-600 mt-1 line-clamp-1">
                              {step.content.replace(/<[^>]*>/g, '').substring(0, 60)}...
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {mediaPlacement?.position !== 'new-step' && mediaPlacement?.stepId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position Within Step
                    </label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setMediaPlacement(prev => prev && prev.stepId ? { stepId: prev.stepId, position: 'before' } : null)}
                        className={cn(
                          "px-4 py-2 border rounded-lg text-sm transition-colors",
                          mediaPlacement?.position === 'before'
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        Before step content
                      </button>
                      <button
                        onClick={() => setMediaPlacement(prev => prev && prev.stepId ? { stepId: prev.stepId, position: 'within' } : null)}
                        className={cn(
                          "px-4 py-2 border rounded-lg text-sm transition-colors",
                          mediaPlacement?.position === 'within'
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        Within step content
                      </button>
                      <button
                        onClick={() => setMediaPlacement(prev => prev && prev.stepId ? { stepId: prev.stepId, position: 'after' } : null)}
                        className={cn(
                          "px-4 py-2 border rounded-lg text-sm transition-colors",
                          mediaPlacement?.position === 'after'
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        After step content
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowMediaPlacementModal(false);
                    setMediaPlacement(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (mediaPlacement) {
                      setShowMediaPlacementModal(false);
                      // The placement is saved, user can now send message with media
                      toast.success('Media placement selected. Add your message and send.');
                    }
                  }}
                  disabled={!mediaPlacement}
                  className="px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step Edit Modal */}
      <AnimatePresence mode="wait">
        {editingStepModal && (() => {
          const stepToShow = tutorialState.steps.find(s => s.id === editingStepModal);
          console.log('🔍 Checking modal render - stepId:', editingStepModal);
          console.log('🔍 Step found:', stepToShow ? `YES (${stepToShow.title})` : 'NO');
          console.log('🔍 Content length:', editingStepContent.length);
          
          if (!stepToShow) {
            console.error('❌ Step not found! Available steps:', tutorialState.steps.map(s => s.id));
            return null;
          }
          
          console.log('✅ Rendering StepEditModal');
          return (
            <StepEditModal
              key={`edit-modal-${editingStepModal}`}
              step={stepToShow}
              content={editingStepContent}
              onContentChange={(newContent) => {
                setEditingStepContent(newContent);
              }}
              onSave={() => {
                const currentStepId = editingStepModal;
                console.log('💾 Saving step:', currentStepId);
                if (currentStepId) {
                  handleStepEdit(currentStepId, 'content', editingStepContent);
                  setEditingStepModal(null);
                  setEditingStepContent('');
                  toast.success('Step updated successfully');
                }
              }}
              onClose={() => {
                console.log('🚪 Closing modal');
                setEditingStepModal(null);
                setEditingStepContent('');
              }}
            />
          );
        })()}
      </AnimatePresence>

      {/* Frame Selector Modal */}
      {frameSelectorOpen && frameSelectorStepId && (
        <FrameSelector
          isOpen={frameSelectorOpen}
          onClose={() => {
            setFrameSelectorOpen(false);
            setFrameSelectorStepId(null);
          }}
          frames={extractedFrames}
          stepId={frameSelectorStepId}
          stepTitle={tutorialState.steps.find(s => s.id === frameSelectorStepId)?.title || 'Step'}
          currentFrames={getCurrentFramesInStep(frameSelectorStepId)}
          onSelectFrames={handleSelectFrames}
        />
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <CategoryManager
          onClose={() => {
            setShowCategoryManager(false);
            loadCategories(); // Reload categories after management
          }}
          onCategorySelect={(category) => {
            setTutorialState(prev => ({ ...prev, category: category.name }));
            setShowCategoryManager(false);
            loadCategories(); // Reload to get updated list
          }}
          isModal={true}
        />
      )}

      {/* Glossary Extraction Modal */}
      <GlossaryExtractionModal
        isOpen={showGlossaryExtraction}
        onClose={() => setShowGlossaryExtraction(false)}
        tutorial={{
          title: tutorialState.title,
          description: tutorialState.description,
          steps: tutorialState.steps.map((step: TutorialStep) => ({
            title: step.title,
            content: step.content || '',
            subsections: step.subsections
          }))
        }}
      />
    </div>
  );
};

// Step Edit Modal Component
interface StepEditModalProps {
  step: TutorialStep | undefined;
  content: string;
  onContentChange: (content: string) => void;
  onSave: () => void;
  onClose: () => void;
}

const StepEditModal: React.FC<StepEditModalProps> = ({
  step,
  content,
  onContentChange,
  onSave,
  onClose
}) => {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [isProcessingPaste, setIsProcessingPaste] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // Normalize content: convert plain text to HTML if needed
  const normalizeContentForEditor = (content: string): string => {
    if (!content) return '';
    
    // If content is already HTML (contains tags), return as-is after cleaning
    if (content.includes('<') && content.includes('>')) {
      let cleanContent = content;
      cleanContent = cleanContent.replace(/dir="rtl"/gi, 'dir="ltr"');
      cleanContent = cleanContent.replace(/style="[^"]*direction:\s*rtl[^"]*"/gi, 'style="direction: ltr"');
      return cleanContent;
    }
    
    // Split by newlines to process each line
    const lines = content.split('\n');
    const processedLines: string[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trim();
      if (!line) {
        // Empty line - add paragraph break
        if (processedLines.length > 0 && !processedLines[processedLines.length - 1].endsWith('</p>')) {
          processedLines.push('</p>');
        }
        processedLines.push('<p>');
        continue;
      }
      
      // Check if line looks like a numbered list item (e.g., "1. **Title:** content")
      const numberedListMatch = line.match(/^(\d+)\.\s*(.+)$/);
      if (numberedListMatch) {
        const num = numberedListMatch[1];
        let rest = numberedListMatch[2];
        
        // Convert markdown in the rest of the line
        rest = rest.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        rest = rest.replace(/\*(.+?)\*/g, '<em>$1</em>');
        
        // Check if rest starts with bold (after conversion)
        const boldMatch = rest.match(/^(<strong>.*?<\/strong>)\s*(.+)$/);
        if (boldMatch) {
          const boldPart = boldMatch[1];
          const contentPart = boldMatch[2];
          const boldText = boldPart.replace(/<\/?strong>/g, '');
          processedLines.push(`<p><strong>${num}. ${boldText}</strong> ${contentPart}</p>`);
        } else {
          processedLines.push(`<p><strong>${num}.</strong> ${rest}</p>`);
        }
        continue;
      }
      
      // Check if line starts with bullet point
      if (line.startsWith('- ') || line.startsWith('* ')) {
        line = line.replace(/^[-*]\s+/, '• ');
        // Convert markdown
        line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        line = line.replace(/\*(.+?)\*/g, '<em>$1</em>');
        processedLines.push(`<p>${line}</p>`);
        continue;
      }
      
      // Convert markdown in regular lines
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      line = line.replace(/\*(.+?)\*/g, '<em>$1</em>');
      
      // Regular paragraph
      if (processedLines.length === 0 || processedLines[processedLines.length - 1].endsWith('</p>')) {
        processedLines.push(`<p>${line}</p>`);
      } else {
        // Append to previous paragraph with line break
        const lastLine = processedLines[processedLines.length - 1];
        processedLines[processedLines.length - 1] = lastLine.replace('</p>', `<br>${line}</p>`);
      }
    }
    
    return processedLines.join('');
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && content !== editor.innerHTML) {
      if (content) {
        const normalizedContent = normalizeContentForEditor(content);
        editor.innerHTML = normalizedContent;
      } else {
        editor.innerHTML = '';
      }
      editor.style.direction = 'ltr';
      editor.style.textAlign = 'left';
      editor.style.unicodeBidi = 'normal';
    }
  }, [content]);

  const handleFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onContentChange(editorRef.current.innerHTML || '');
    }
  };

  const handleContentChange = () => {
    const editor = editorRef.current;
    if (editor) {
      editor.style.direction = 'ltr';
      editor.style.textAlign = 'left';
      editor.style.unicodeBidi = 'normal';
      onContentChange(editor.innerHTML || '');
    }
  };

  const compressImage = (file: File, maxWidth: number = 1920, maxHeight: number = 1080, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: file.type, lastModified: Date.now() }));
          } else {
            reject(new Error('Failed to compress image'));
          }
        }, file.type, quality);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePaste = async (event: React.ClipboardEvent<HTMLDivElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
    if (imageItems.length === 0) return;

    event.preventDefault();
    setIsProcessingPaste(true);

    try {
      for (const item of imageItems) {
        const file = item.getAsFile();
        if (!file) continue;
        let processedFile = file;
        if (file.size > 1024 * 1024) {
          try {
            processedFile = await compressImage(file);
          } catch (error) {
            console.warn('Failed to compress pasted image:', error);
          }
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          if (dataUrl && editorRef.current) {
            const img = document.createElement('img');
            img.src = dataUrl;
            img.alt = 'Pasted image';
            img.className = 'max-w-full h-auto rounded-lg my-4';
            
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              range.deleteContents();
              range.insertNode(img);
              range.setStartAfter(img);
              range.collapse(true);
              selection.removeAllRanges();
              selection.addRange(range);
            } else {
              editorRef.current.appendChild(img);
            }
            handleContentChange();
          }
        };
        reader.readAsDataURL(processedFile);
      }
    } catch (error) {
      console.error('Error processing pasted image:', error);
    } finally {
      setIsProcessingPaste(false);
    }
  };

  const handleMediaSelect = (mediaFile: MediaFile) => {
    if (editorRef.current) {
      const img = document.createElement('img');
      img.src = mediaFile.data;
      img.alt = mediaFile.alt || mediaFile.name;
      img.className = 'max-w-full h-auto rounded-lg my-4';
      
      if (mediaFile.caption) {
        const caption = document.createElement('p');
        caption.className = 'text-sm text-gray-600 text-center italic mb-4';
        caption.textContent = mediaFile.caption;
        const container = document.createElement('div');
        container.className = 'my-4';
        container.appendChild(img);
        container.appendChild(caption);
        editorRef.current.appendChild(container);
      } else {
        editorRef.current.appendChild(img);
      }
      handleContentChange();
    }
    setShowMediaLibrary(false);
  };

  if (!step) {
    console.error('StepEditModal: step is undefined', step);
    return null;
  }

  console.log('StepEditModal rendering for step:', step.id);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 overflow-hidden"
    >
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full mx-auto mt-10 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] px-6 py-4 text-white flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">{step.title || 'Edit Step'}</h3>
            <p className="text-sm text-white/80 mt-1">Edit step content with rich text formatting</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="border-b border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleFormat('bold')}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Bold"
            >
              <Bold className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => handleFormat('italic')}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Italic"
            >
              <Italic className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => handleFormat('insertUnorderedList')}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Bullet List"
            >
              <List className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => {
                const url = prompt('Enter URL:');
                if (url) handleFormat('createLink', url);
              }}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Insert Link"
            >
              <Link className="w-4 h-4 text-gray-600" />
            </button>
            <div className="w-px h-6 bg-gray-300 mx-2" />
            <button
              onClick={() => setShowMediaLibrary(true)}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              title="Insert Media"
            >
              <ImageIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Add Image</span>
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-y-auto p-6 relative">
          <div
            ref={editorRef}
            contentEditable
            className="min-h-[400px] p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent outline-none prose prose-lg max-w-none tutorial-content"
            onInput={handleContentChange}
            onPaste={handlePaste}
            style={{
              lineHeight: '1.75',
              fontSize: '16px',
              direction: 'ltr',
              textAlign: 'left',
              unicodeBidi: 'normal',
              whiteSpace: 'pre-wrap'
            }}
            dir="ltr"
          />
          
          {!content && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-gray-400 text-sm">Start typing your content or paste images...</p>
            </div>
          )}
          
          {isProcessingPaste && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-white bg-opacity-75">
              <div className="flex items-center space-x-2 text-blue-600">
                <Loader className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Processing pasted image...</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all"
          >
            Save Changes
          </button>
        </div>

        {/* Media Library Modal */}
        {showMediaLibrary && (
          <MediaLibrary
            onSelect={handleMediaSelect}
            onClose={() => setShowMediaLibrary(false)}
            isModal={true}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

// Extend Window interface for Speech Recognition
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

export default AIAssistedBuilder;
